import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from "react-dom";
import axios from "axios";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Polygon, CircleMarker, Tooltip as LeafletTooltip, useMapEvents, useMap } from 'react-leaflet';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { useTeams } from "@/hooks/useTeams";
import { Radio, Plus, RefreshCw, MapPin, Users, Clock, CheckCircle2, AlertTriangle, X, Play, Check, Ban, Undo2, ShieldCheck, Search, Crosshair, Trash2, Settings, Edit2, Info } from 'lucide-react';
import { toast } from "sonner";
import { useAuthorization } from "@/auth/AuthorizationContext";
import { mediaUrl } from "@/utils/mediaUrl";

// Leaflet marker ikonları (ProtokolParklarTab ile aynı CDN fix)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LOCATION_KINDS_UI = [
  { value: "park", label: "Kayıtlı Park" },
  { value: "pasif_alan", label: "Pasif Alan" },
  { value: "protokol", label: "Protokollü Park" },
  { value: "serbest", label: "Serbest İşaret" },
];

// Harita katmanları — standart (OSM) ve uydu (Esri World Imagery)
const TILE_LAYERS = {
  street: {
    label: "Harita",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "&copy; OpenStreetMap",
  },
  satellite: {
    label: "Uydu",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
  },
};

// ---------------------------------------------------------------------------
// Sabitler — backend modules/operasyon/models.py ile birebir
// ---------------------------------------------------------------------------

const TASK_CATEGORIES = [
  { value: "temizlik", label: "Temizlik" },
  { value: "yesil_alan", label: "Yeşil Alan" },
  { value: "teknik", label: "Teknik" },
  { value: "acil", label: "Acil Müdahale" },
  { value: "serbest", label: "Serbest Görev" },
];

const TASK_TYPES_BY_CAT = {
  temizlik: ["Genel Temizlik", "Çöp Toplama", "Mıntıka Temizliği"],
  yesil_alan: ["Çim Biçimi", "Budama", "Sulama", "İlaçlama", "Kurumuş Ağaç"],
  teknik: ["Havuz Temizliği", "Sulama Arızası", "Elektrik Arızası"],
  acil: ["Devrilmiş Ağaç", "Tehlikeli Dal", "Vatandaş Şikayeti"],
  serbest: [],
};

const PRIORITIES = [
  { value: "bilgilendirme", label: "🔵 Bilgilendirme", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  { value: "normal", label: "🟡 Normal", cls: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  { value: "acil", label: "🟠 Acil", cls: "bg-orange-100 text-orange-800 border-orange-400" },
  { value: "cok_acil", label: "🔴 Çok Acil", cls: "bg-red-600 text-white border-red-700" },
];

// Liste sıralaması — çok acil en üstte
const PRIO_ORDER = { cok_acil: 0, acil: 1, normal: 2, bilgilendirme: 3 };

const ORDER_TYPES = [
  { value: "bilgilendirme", label: "Bilgilendirme" },
  { value: "planlamaya_dahil", label: "Planlamaya Dahil Et" },
  { value: "zorunlu_uygula", label: "Hemen Uygula" },
];

const OPERATION_EFFECTS = [
  { value: "", label: "— Seçilmedi —" },
  { value: "destek", label: "Destek Görevi" },
  { value: "kismi", label: "Kısmi Görevlendirme" },
  { value: "gecici", label: "Geçici Görevlendirme" },
  { value: "tam_gun", label: "Tam Gün Görevlendirme" },
];

const SOURCES = [
  { value: "", label: "— Seçilmedi —" },
  { value: "yonetici_talimati", label: "Yönetici Talimatı" },
  { value: "baskan_talimati", label: "Başkan Talimatı" },
  { value: "vatandas_sikayeti", label: "Vatandaş Şikayeti" },
  { value: "cagri_merkezi", label: "Çağrı Merkezi" },
  { value: "cimer", label: "CİMER" },
  { value: "denetim", label: "Denetim Sonucu" },
  { value: "plan_disi", label: "Faaliyet Planı Dışı" },
  { value: "diger", label: "Diğer" },
];

const STATUS_INFO = {
  iletildi: { label: "İletildi", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  goruldu: { label: "Görüldü", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  basladi: { label: "Başladı", cls: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  devam: { label: "Devam Ediyor", cls: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  tamamlandi: { label: "Tamamlandı", cls: "bg-green-100 text-green-700 border-green-300" },
  onaylandi: { label: "Onaylandı", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  iptal: { label: "İptal Edildi", cls: "bg-red-100 text-red-700 border-red-300" },
  geri_cekildi: { label: "Geri Çekildi", cls: "bg-amber-100 text-amber-700 border-amber-300" },
};

// Tüm görev türleri — kategori bağımsız, gruplu aranabilir dropdown için.
// Çim Biçimi / Budama / Sulama (Yeşil Alan) dahil hepsi tek listede.
const ALL_TASK_TYPES = TASK_CATEGORIES
  .filter(c => (TASK_TYPES_BY_CAT[c.value] || []).length > 0)
  .flatMap(c => (TASK_TYPES_BY_CAT[c.value] || []).map(t => ({ id: t, name: t, group: c.label, cat: c.value })));

const ROLE_LABELS = {
  mudur: "Müdür", mudur_yardimcisi: "Müdür Yardımcısı", bolge_muhendisi: "Bölge Mühendisi",
  formen: "Formen", park_gorevlisi: "Park Görevlisi", baskan: "Başkan", admin: "Admin",
};
const roleOf = (p) => (p?.role_raw || p?.role || "").toString();
const roleLabel = (slug) => ROLE_LABELS[slug] || (slug || "Diğer");

// Sorumlu listesini hiyerarşik sırala: Park Görevlisi → Formen → BM → MY → Müdür
const ROLE_RANK = { park_gorevlisi: 0, formen: 1, bolge_muhendisi: 2, mudur_yardimcisi: 3, mudur: 4, baskan: 5, admin: 6 };
const roleRank = (p) => (ROLE_RANK[roleOf(p)] ?? 99);
// Rol slug'ından rank (düşük = saha/operasyonel, yüksek = üst yönetim).
const rankOfRole = (slug) => (ROLE_RANK[slug] ?? 99);

// Nokta poligon içinde mi? (ray-casting) — pt=[lat,lng], poly=[[lat,lng],...]
function pointInPolygon(pt, poly) {
  if (!Array.isArray(poly) || poly.length < 3) return false;
  const x = pt[0], y = pt[1];
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
    const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

// Operasyon Merkezi harita marker rengi (gecikme > tamamlanan/iptal > öncelik)
function markerColor(o) {
  if (o.is_overdue) return "#111827";                                  // ⚫ geciken
  if (o.status === "tamamlandi" || o.status === "onaylandi") return "#16a34a"; // 🟢
  if (o.status === "iptal" || o.status === "geri_cekildi") return "#9ca3af";   // ⚪
  if (o.priority === "cok_acil") return "#b91c1c";                     // 🔴 koyu kırmızı
  if (o.priority === "acil") return "#f97316";                         // 🟠 canlı turuncu
  if (o.priority === "normal") return "#eab308";                       // 🟡
  return "#2563eb";                                                    // 🔵 bilgilendirme
}
const MAP_LEGEND = [
  { c: "#b91c1c", l: "Çok Acil" }, { c: "#f97316", l: "Acil" }, { c: "#eab308", l: "Normal" },
  { c: "#2563eb", l: "Bilgi" }, { c: "#16a34a", l: "Tamamlanan" }, { c: "#111827", l: "Geciken" },
  { c: "#d946ef", l: "İşaret noktası (not)" },
];

// Görev geçmişi olay etiketleri (Türkçe)
const EVENT_LABELS = {
  created: "Oluşturuldu", seen: "Görüldü", start: "Başladı", complete: "Tamamladı",
  approve: "Onayladı", delegated: "Alt görevlendirdi", deleted: "Sildi",
  planned: "Planladı", iptal: "İptal etti", geri_cekildi: "Geri çekti",
  edited: "Düzenleyip yeniden gönderdi", removed: "Görevliyi çıkardı",
  is_engeli: "İş engeli bildirdi",
};
function eventLabel(t) { return EVENT_LABELS[t] || t; }
// Geçmişte "önemli olaylar" (kilometre taşı) — kişi bazlı görüldü/başladı/tamamladı rutindir, varsayılan gizli
const MILESTONE_EVENTS = new Set(["created", "delegated", "approve", "iptal", "geri_cekildi", "edited", "planned", "removed", "deleted", "is_engeli"]);
const isMilestoneEvent = (t) => MILESTONE_EVENTS.has(t);

// Alt görevlendirme notundan talimat kısmını ayıkla ("… — Görevlendirildi: isimler")
function delegInstruction(note) {
  if (!note) return null;
  const idx = note.indexOf("Görevlendirildi:");
  if (idx === -1) return note.trim() || null;
  const pre = note.slice(0, idx).replace(/[—-]\s*$/, "").trim();
  return pre || null;
}
// Alt görevlendirme notundan isim listesini ayıkla
function delegNames(note) {
  if (!note) return [];
  const idx = note.indexOf("Görevlendirildi:");
  if (idx === -1) return [];
  return note.slice(idx + "Görevlendirildi:".length).split(",").map(s => s.trim()).filter(Boolean);
}

function orderTypeLabel(v) { return (ORDER_TYPES.find(x => x.value === v) || {}).label || v; }
function effectLabel(v) { return v ? ((OPERATION_EFFECTS.find(x => x.value === v) || {}).label || v) : ""; }
function sourceLabel(v) { return v ? ((SOURCES.find(x => x.value === v) || {}).label || v) : ""; }
function mapsDirUrl(lat, lng) { return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`; }

// Görsel büyük önizleme (lightbox). DraggableModal transform kullandığı için
// portal ile doğrudan body'e basılır; aksi halde fixed overlay modalın içine sıkışır.
// İndirmeden, detaylı bakmak için: tıkla → büyük aç, dışına/✕'e tıkla → kapat.
// Çoklu görselde ←/→ ok tuşları veya yan oklarla sıradakine geçilir.
function ImageLightbox({ items, index, onIndex, onClose }) {
  const list = Array.isArray(items) ? items : [];
  const count = list.length;
  const safeIdx = Math.min(Math.max(index || 0, 0), Math.max(count - 1, 0));
  const go = useCallback((delta) => {
    if (count <= 1) return;
    onIndex((safeIdx + delta + count) % count);
  }, [count, safeIdx, onIndex]);
  useEffect(() => {
    if (count === 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [count, go, onClose]);
  if (count === 0) return null;
  const cur = list[safeIdx];
  const url = typeof cur === "string" ? cur : cur?.url;
  const alt = typeof cur === "string" ? "" : (cur?.name || "");
  const isVideo = typeof cur === "object" && cur?.type === "video";
  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[3000] bg-black/85 flex items-center justify-center p-4"
      style={{ cursor: "zoom-out" }}
    >
      {isVideo ? (
        <video
          src={url}
          controls
          autoPlay
          onClick={(e) => e.stopPropagation()}
          className="max-h-[92vh] max-w-[94vw] rounded shadow-2xl bg-black"
          style={{ cursor: "default" }}
        />
      ) : (
        <img
          src={url}
          alt={alt}
          onClick={(e) => e.stopPropagation()}
          className="max-h-[92vh] max-w-[94vw] object-contain rounded shadow-2xl"
          style={{ cursor: "default" }}
        />
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label="Kapat"
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/90 text-gray-800 hover:bg-white flex items-center justify-center shadow-lg text-lg"
      >
        ✕
      </button>
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Önceki"
            className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/90 text-gray-800 hover:bg-white flex items-center justify-center shadow-lg text-2xl"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Sonraki"
            className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/90 text-gray-800 hover:bg-white flex items-center justify-center shadow-lg text-2xl"
          >
            ›
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-sm">
            {safeIdx + 1} / {count}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
function locCoords(l) {
  if (l.pin_lat != null && l.pin_lng != null) return [Number(l.pin_lat), Number(l.pin_lng)];
  if (l.lat != null && l.lng != null) return [Number(l.lat), Number(l.lng)];
  return null;
}
function evidenceList(o) {
  const e = [];
  if (o.need_start_photo) e.push("Başlangıç fotoğrafı");
  if (o.need_end_photo) e.push("Bitiş fotoğrafı");
  if (o.need_start_video) e.push("Başlangıç videosu");
  if (o.need_end_video) e.push("Bitiş videosu");
  if (o.need_start_gps) e.push("Başlangıç GPS");
  if (o.need_end_gps) e.push("Bitiş GPS");
  return e;
}

function priorityInfo(p) { return PRIORITIES.find(x => x.value === p) || PRIORITIES.find(x => x.value === "normal"); }
function statusInfo(s) { return STATUS_INFO[s] || STATUS_INFO.iletildi; }
function catLabel(c) { return (TASK_CATEGORIES.find(x => x.value === c) || {}).label || c; }

// Bugünün tarihi (YYYY-MM-DD) — yeni emir varsayılan tarihi
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// "2026-06-18" / ISO → "18-06-2026" (gün-ay-yıl); saat varsa korunur
function fmtTR(v) {
  if (!v) return "";
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  const tr = `${m[3]}-${m[2]}-${m[1]}`;
  const time = s.slice(10).replace("T", " ").trim().slice(0, 6);
  return time ? `${tr} ${time}` : tr;
}

// Yüklenen görselin görüntüleme URL'i — ortak yardımcı (@/utils/mediaUrl).
// Buradaki yerel kopya kaldırıldı: aynı kural birden çok yerde tekrarlanınca
// yeni ekranlar ham `/uploads/...` kullanıp sessizce 401 alıyordu.

// ---------------------------------------------------------------------------
// Ana bileşen
// ---------------------------------------------------------------------------

export default function OperasyonTab({ API, personnelId, personnel = [], parks = [] }) {
  const authz = useAuthorization();
  const canCreate = authz.has?.("operasyon.olustur");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false); // başlık (i) açıklaması
  const [editOrder, setEditOrder] = useState(null); // geri çekilen emri düzenle+yeniden gönder
  const [detailId, setDetailId] = useState(null);
  const [slaOpen, setSlaOpen] = useState(false);
  const [view, setView] = useState("list"); // list | map
  const [features, setFeatures] = useState({ executor_model: true }); // operasyon özellik anahtarları

  const apiBase = `${API}/api/operasyon`;

  // Özellik anahtarlarını (yürütücü modeli aç/kapa) bir kez çek
  useEffect(() => {
    let alive = true;
    axios.get(`${apiBase}/features`)
      .then(r => { if (alive && r.data && typeof r.data === "object") setFeatures({ executor_model: r.data.executor_model !== false }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [apiBase]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(apiBase);
      setOrders(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Operasyon emirleri yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Canlı SSE: operasyon olaylarında listeyi tazele (global EventSource App'te kurulu)
  useEffect(() => {
    const handler = () => loadOrders();
    const onDeleted = (e) => {
      loadOrders();
      const oid = e?.detail?.order_id;
      if (oid && oid === detailId) setDetailId(null); // açık stale detayı kapat
    };
    window.addEventListener("operation_assigned", handler);
    window.addEventListener("operation_updated", handler);
    window.addEventListener("operation_deleted", onDeleted);
    return () => {
      window.removeEventListener("operation_assigned", handler);
      window.removeEventListener("operation_updated", handler);
      window.removeEventListener("operation_deleted", onDeleted);
    };
  }, [loadOrders, detailId]);

  // Alt hiyerarşideki personel (atama için)
  const subordinates = useMemo(() => {
    const list = Array.isArray(personnel) ? personnel : [];
    if (!authz.isInChain) return list;
    return list.filter(p => p && p.id && authz.isInChain(p.id));
  }, [personnel, authz]);

  // --- Liste filtreleri (EMRİ VERENE göre — kademeli MY → BM → Formen) ---
  const [search, setSearch] = useState("");
  const [fMY, setFMY] = useState("all");
  const [fBM, setFBM] = useState("all");
  const [fFormen, setFFormen] = useState("all");
  const [fPriority, setFPriority] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [onlyMine, setOnlyMine] = useState(false); // sadece benim verdiğim emirler

  const personById = useMemo(() => {
    const m = new Map();
    (personnel || []).forEach(p => { if (p && p.id) m.set(p.id, p); });
    (subordinates || []).forEach(p => { if (p && p.id && !m.has(p.id)) m.set(p.id, p); });
    return m;
  }, [personnel, subordinates]);

  // Emri verenin (oluşturanın) hiyerarşi zinciri — App.resolveHierarchyIds ile aynı mantık
  const branchOf = useCallback((creatorId) => {
    const person = personById.get(creatorId);
    if (!person) return { role: null, myId: null, bmId: null, formenId: null };
    const byId = (id) => personById.get(id);
    const role = roleOf(person);
    let myId = person.mudur_yardimcisi_id || null;
    let bmId = person.bolge_muhendisi_id || null;
    let formenId = person.formen_id || null;
    if (role === "mudur_yardimcisi") {
      myId = person.id;
    } else if (role === "bolge_muhendisi") {
      bmId = person.id;
      if (!myId && person.parent_id) { const par = byId(person.parent_id); if (par && roleOf(par) === "mudur_yardimcisi") myId = par.id; }
    } else if (role === "formen") {
      formenId = person.id;
      if (!bmId && person.parent_id) { const par = byId(person.parent_id); if (par && roleOf(par) === "bolge_muhendisi") bmId = par.id; }
      if (!myId && bmId) { const bm = byId(bmId); myId = bm?.mudur_yardimcisi_id || myId; }
    } else if (role === "park_gorevlisi") {
      if (!formenId && person.parent_id) { const par = byId(person.parent_id); if (par && roleOf(par) === "formen") formenId = par.id; }
      if (!bmId && formenId) { const f = byId(formenId); bmId = f?.bolge_muhendisi_id || bmId; }
      if (!myId && bmId) { const bm = byId(bmId); myId = bm?.mudur_yardimcisi_id || myId; }
    }
    return { role, myId, bmId, formenId };
  }, [personById]);

  const nameOf = useCallback((id) => personById.get(id)?.name || "—", [personById]);

  // Kademeli seçenekler — yalnızca emir vermiş kişilerin dalları görünür
  const myOptions = useMemo(() => {
    const m = new Map();
    orders.forEach(o => { const b = branchOf(o.created_by_id); if (b.myId) m.set(b.myId, nameOf(b.myId)); });
    return [...m].map(([id, name]) => ({ id, name }));
  }, [orders, branchOf, nameOf]);
  const bmOptions = useMemo(() => {
    const m = new Map();
    orders.forEach(o => { const b = branchOf(o.created_by_id); if (b.bmId && (fMY === "all" || b.myId === fMY)) m.set(b.bmId, nameOf(b.bmId)); });
    return [...m].map(([id, name]) => ({ id, name }));
  }, [orders, branchOf, nameOf, fMY]);
  const formenOptions = useMemo(() => {
    const m = new Map();
    orders.forEach(o => { const b = branchOf(o.created_by_id); if (b.role === "formen" && b.formenId && (fMY === "all" || b.myId === fMY) && (fBM === "all" || b.bmId === fBM)) m.set(b.formenId, nameOf(b.formenId)); });
    return [...m].map(([id, name]) => ({ id, name }));
  }, [orders, branchOf, nameOf, fMY, fBM]);

  // Seçim artık seçeneklerde yoksa sıfırla (kademeli daralma)
  useEffect(() => { if (fMY !== "all" && !myOptions.some(x => x.id === fMY)) setFMY("all"); }, [fMY, myOptions]);
  useEffect(() => { if (fBM !== "all" && !bmOptions.some(x => x.id === fBM)) setFBM("all"); }, [fBM, bmOptions]);
  useEffect(() => { if (fFormen !== "all" && !formenOptions.some(x => x.id === fFormen)) setFFormen("all"); }, [fFormen, formenOptions]);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    const list = orders.filter(o => {
      if (onlyMine && o.created_by_id !== personnelId) return false;
      const b = branchOf(o.created_by_id);
      if (fMY !== "all" && b.myId !== fMY) return false;
      if (fBM !== "all" && b.bmId !== fBM) return false;
      if (fFormen !== "all" && !(b.formenId === fFormen || o.created_by_id === fFormen)) return false;
      if (fPriority !== "all" && o.priority !== fPriority) return false;
      if (fStatus !== "all" && o.status !== fStatus) return false;
      if (q) {
        const hay = `${o.title || ""} ${o.location_label || ""} ${o.created_by_name || ""}`.toLocaleLowerCase("tr");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return list.sort((a, b) => {
      const pa = PRIO_ORDER[a.priority] ?? 9, pb = PRIO_ORDER[b.priority] ?? 9;
      if (pa !== pb) return pa - pb;
      return (b.scheduled_date || "").localeCompare(a.scheduled_date || "");
    });
  }, [orders, branchOf, fMY, fBM, fFormen, fPriority, fStatus, search, onlyMine, personnelId]);

  const activeFilterCount = [fMY, fBM, fFormen, fPriority, fStatus].filter(v => v !== "all").length + (search.trim() ? 1 : 0) + (onlyMine ? 1 : 0);
  const clearFilters = () => { setSearch(""); setFMY("all"); setFBM("all"); setFFormen("all"); setFPriority("all"); setFStatus("all"); setOnlyMine(false); };

  return (
    <div className="space-y-4">
      {/* Başlık */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Radio className="h-6 w-6 text-green-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-1.5">
              Anlık Görevlendirme
              <span className="relative font-normal">
                <button type="button" onClick={() => setShowInfo(v => !v)} className="text-gray-400 hover:text-green-600" title="Bilgi">
                  <Info className="h-4 w-4" />
                </button>
                {showInfo && (
                  <div className="absolute left-0 top-6 z-50 w-72 rounded-md border bg-white p-2.5 text-xs text-gray-600 shadow-lg">
                    Sahaya <b>anlık görev</b> verme katmanı — istenirse <b>haftalık plana da dahil edilebilir</b>. Haftalık planın yerine geçmez, yanında çalışır.
                    <button type="button" onClick={() => setShowInfo(false)} className="block mt-1.5 text-blue-600 hover:underline">Kapat</button>
                  </div>
                )}
              </span>
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border overflow-hidden text-sm">
            <button type="button" onClick={() => setView("list")} className={`px-3 py-1 ${view === "list" ? "bg-green-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>Liste</button>
            <button type="button" onClick={() => setView("map")} className={`px-3 py-1 ${view === "map" ? "bg-green-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>Harita</button>
            <button type="button" onClick={() => setView("report")} className={`px-3 py-1 ${view === "report" ? "bg-green-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>Rapor</button>
          </div>
          <Button variant="outline" size="sm" onClick={loadOrders} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {authz.has?.("operasyon.sla_yonet") && (
            <Button variant="outline" size="sm" onClick={() => setSlaOpen(true)} title="SLA Ayarları">
              <Settings className="h-4 w-4" />
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus className="h-4 w-4 mr-1" /> Anlık Yeni Görev
            </Button>
          )}
        </div>
      </div>

      {/* Harita görünümü — Müdür Operasyon Merkezi */}
      {view === "map" && (
        <OperationCenterMap apiBase={apiBase} parks={parks} onOpen={(id) => setDetailId(id)} />
      )}

      {/* Rapor görünümü — KPI */}
      {view === "report" && <OperationReport apiBase={apiBase} />}

      {/* Liste */}
      {view === "list" && (
        <>
          {/* Filtre çubuğu — emri verene göre (kademeli) */}
          <div className="bg-white border rounded-lg p-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-gray-500">Ara</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Başlık / konum / veren" className="pl-8 h-9" />
              </div>
            </div>
            <div className="min-w-[150px]">
              <Label className="text-xs text-gray-500">Müdür Yard. (Görev Veren)</Label>
              <SearchableSelect value={fMY} onChange={setFMY} allLabel="Tümü" allValue="all" options={myOptions} searchPlaceholder="Ara..." emptyText="Emir veren MY yok" className="h-9" />
            </div>
            <div className="min-w-[150px]">
              <Label className="text-xs text-gray-500">Bölge Müh. (Görev Veren)</Label>
              <SearchableSelect value={fBM} onChange={setFBM} allLabel="Tümü" allValue="all" options={bmOptions} searchPlaceholder="Ara..." emptyText="Emir veren BM yok" className="h-9" />
            </div>
            <div className="min-w-[150px]">
              <Label className="text-xs text-gray-500">Formen (Görev Veren)</Label>
              <SearchableSelect value={fFormen} onChange={setFFormen} allLabel="Tümü" allValue="all" options={formenOptions} searchPlaceholder="Ara..." emptyText="Emir veren formen yok" className="h-9" />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs text-gray-500">Öncelik</Label>
              <SearchableSelect value={fPriority} onChange={setFPriority} allLabel="Tümü" allValue="all" options={PRIORITIES.map(p => ({ id: p.value, name: p.label }))} searchPlaceholder="Ara..." className="h-9" />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs text-gray-500">Durum</Label>
              <SearchableSelect value={fStatus} onChange={setFStatus} allLabel="Tümü" allValue="all" options={Object.entries(STATUS_INFO).map(([k, v]) => ({ id: k, name: v.label }))} searchPlaceholder="Ara..." className="h-9" />
            </div>
            <button type="button" onClick={() => setOnlyMine(v => !v)}
              className={`h-9 px-3 rounded-md border text-xs font-medium whitespace-nowrap ${onlyMine ? "bg-green-600 text-white border-green-700" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              title="Yalnızca benim görevlendirdiğim operasyon emirleri">
              Benim görevlendirdiklerim
            </button>
            <div className="text-xs text-gray-500 pb-2 whitespace-nowrap">
              {filteredOrders.length} / {orders.length} emir
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearFilters} className="ml-2 text-blue-600 hover:underline">Temizle</button>
              )}
            </div>
          </div>

          {orders.length === 0 && !loading ? (
            <Card><CardContent className="py-10 text-center text-gray-400">
              Henüz görev yok.
            </CardContent></Card>
          ) : filteredOrders.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-gray-400">
              Filtreye uygun görev yok.
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearFilters} className="ml-2 text-blue-600 hover:underline">Filtreleri temizle</button>
              )}
            </CardContent></Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {filteredOrders.map(o => {
            const pi = priorityInfo(o.priority);
            const si = statusInfo(o.status);
            return (
              <Card key={o.id} className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setDetailId(o.id)}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-gray-800 line-clamp-2">{o.title}</span>
                    <Badge variant="outline" className={si.cls}>{si.label}</Badge>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <Badge variant="outline" className={pi.cls}>{pi.label}</Badge>
                    {o.is_collective && <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">Toplu Çalışma</Badge>}
                    <span className="text-gray-500">{catLabel(o.task_category)}{o.task_type ? ` · ${o.task_type}` : ""}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {o.location_label && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{o.location_label}{o.location_count > 1 ? ` +${o.location_count - 1}` : ""}</span>}
                    {o.scheduled_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtTR(o.scheduled_date)}</span>}
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{o.assignee_count}</span>
                  </div>
                  {o.order_type === "planlamaya_dahil" && (
                    o.planned_at
                      ? <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">Planlandı: {fmtTR(o.scheduled_date)}</Badge>
                      : <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">Planlanacak</Badge>
                  )}
                  {o.is_overdue && (
                    <div className="flex items-center gap-1 text-xs text-red-600">
                      <AlertTriangle className="h-3 w-3" /> Gecikti
                    </div>
                  )}
                  <div className="text-[11px] text-gray-400">
                    Oluşturan: {o.created_by_name || "—"}
                  </div>
                </CardContent>
              </Card>
            );
              })}
            </div>
          )}
        </>
      )}

      {(createOpen || editOrder) && (
        <CreateOrderDialog
          API={API}
          apiBase={apiBase}
          subordinates={subordinates}
          parks={parks}
          canUploadRef={authz.has?.("operasyon.referans_yukle")}
          editOrder={editOrder}
          features={features}
          onClose={() => { setCreateOpen(false); setEditOrder(null); }}
          onCreated={() => { setCreateOpen(false); setEditOrder(null); loadOrders(); }}
        />
      )}

      {detailId && (
        <OrderDetailDialog
          API={API}
          apiBase={apiBase}
          orderId={detailId}
          personnelId={personnelId}
          authz={authz}
          subordinates={subordinates}
          personnel={personnel}
          features={features}
          parks={parks}
          onClose={() => setDetailId(null)}
          onChanged={loadOrders}
          onEdit={(o) => { setDetailId(null); setEditOrder(o); }}
        />
      )}

      {slaOpen && <SlaConfigDialog API={API} authz={authz} features={features} onFeatures={setFeatures} onClose={() => setSlaOpen(false)} />}
    </div>
  );
}

// Role göre çoklu-seçim dropdown: içinde ara + Tümünü seç/kaldır + checkbox (işaretli = görev dahil)
function PeopleCheckDropdown({ label, roleKey, people, isSelected, onToggle, onToggleAll, unavail, ownerIds }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const isOwner = (id) => !!(ownerIds && ownerIds.has(id));
  const filtered = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr");
    const base = s ? people.filter(p => String(p.name || "").toLocaleLowerCase("tr").includes(s)) : people;
    // Bu parkın sorumluları (⭐) en üste
    return [...base].sort((a, b) => (isOwner(a.id) ? 0 : 1) - (isOwner(b.id) ? 0 : 1));
  }, [people, q, ownerIds]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectable = filtered.filter(p => !unavail.has(p.id));
  const allSel = selectable.length > 0 && selectable.every(p => isSelected(p.id));
  const selCount = people.filter(p => isSelected(p.id)).length;
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={`op-picker-${roleKey}`} onClick={() => setOpen(v => !v)}
        className={`w-full h-9 px-2 rounded-md border text-sm text-left flex items-center justify-between ${selCount > 0 ? "border-green-400 bg-green-50 text-green-800" : "bg-white text-gray-700"}`}>
        <span className="truncate">{label}{selCount > 0 ? ` (${selCount})` : ""}</span>
        <span className="text-gray-400 ml-1">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-white border rounded-md shadow-lg">
          <div className="p-1 border-b flex items-center gap-1">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="İsim ara"
              className="flex-1 h-7 px-2 text-xs border rounded" />
            <button type="button" onClick={() => onToggleAll(selectable)}
              className="text-[11px] text-emerald-700 hover:underline whitespace-nowrap px-1">
              {allSel ? "Tümünü kaldır" : "Tümünü seç"}
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && <p className="text-xs text-gray-400 p-2">Kişi yok.</p>}
            {filtered.map(p => {
              const izin = unavail.get(p.id);
              return (
                <label key={p.id} data-testid={`op-person-${p.id}`} className={`flex items-center gap-2 px-2 py-1 text-xs ${izin ? "text-gray-400 cursor-not-allowed bg-gray-50" : "cursor-pointer hover:bg-green-50"}`}>
                  <input type="checkbox" checked={isSelected(p.id)} disabled={!!izin} onChange={() => onToggle(p)} />
                  <span>{isOwner(p.id) ? "⭐ " : ""}{p.name}</span>
                  {izin && <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">İzinli</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Oluşturma dialog'u
// ---------------------------------------------------------------------------

function CreateOrderDialog({ API, apiBase, subordinates, parks, canUploadRef, features = { executor_model: true }, onClose, onCreated, editOrder = null }) {
  const isEdit = !!editOrder;
  const [saving, setSaving] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [passiveAreas, setPassiveAreas] = useState([]);
  const [protokolParks, setProtokolParks] = useState([]);
  const [unavail, setUnavail] = useState(() => new Map()); // izinli/raporlu (o tarihte) → seçilemez

  // Pasif alan + protokol park listelerini modal açılışında yükle
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/passive-areas`).then(r => { if (alive) setPassiveAreas(Array.isArray(r.data) ? r.data : []); }).catch(() => {});
    axios.get(`${API}/api/protokol-parklar`).then(r => { if (alive) setProtokolParks(Array.isArray(r.data) ? r.data : []); }).catch(() => {});
    return () => { alive = false; };
  }, [API]);

  // Serbest harita başlangıç merkezi — koordinatı olan ilk park, yoksa Ankara
  const mapCenter = useMemo(() => {
    const p = (parks || []).find(x => x.latitude != null && x.longitude != null);
    return p ? [Number(p.latitude), Number(p.longitude)] : [39.925, 32.866];
  }, [parks]);
  const [form, setForm] = useState(() => editOrder ? {
    title: editOrder.title || "", description: editOrder.description || "",
    task_category: editOrder.task_category || "temizlik", task_type: editOrder.task_type || "",
    priority: editOrder.priority || "normal", order_type: editOrder.order_type || "zorunlu_uygula",
    operation_effect: editOrder.operation_effect || "", requires_approval: !!editOrder.requires_approval,
    is_collective: !!editOrder.is_collective,
    source: editOrder.source || "",
    location_kind: (editOrder.locations && editOrder.locations[0]?.kind) || "park",
    location_ref_id: "", location_label: "", location_lat: null, location_lng: null,
    locations: (editOrder.locations || []).map(l => ({
      kind: l.kind, ref_id: l.ref_id || null, uid: l.uid || null, label: l.label || "",
      lat: l.lat ?? null, lng: l.lng ?? null,
      pin: (l.pin_lat != null && l.pin_lng != null) ? [Number(l.pin_lat), Number(l.pin_lng)] : undefined,
      // Sınırları koru: düzenleme/yeniden gönderde çizili alan haritada görünmeye devam etsin
      boundaries: Array.isArray(l.boundaries) && l.boundaries.length > 2 ? l.boundaries : null,
      note: l.note || "",
    })),
    scheduled_date: editOrder.scheduled_date || todayISO(),
    start_time: editOrder.start_time || "", end_time: editOrder.end_time || "", due_date: editOrder.due_date || "",
    need_start_photo: !!editOrder.need_start_photo, need_end_photo: !!editOrder.need_end_photo,
    need_start_video: !!editOrder.need_start_video, need_end_video: !!editOrder.need_end_video,
    need_start_gps: !!editOrder.need_start_gps, need_end_gps: !!editOrder.need_end_gps,
    leader_personnel_id: editOrder.leader_personnel_id || "",
    reference_media: Array.isArray(editOrder.reference_media) ? editOrder.reference_media : [],
    reference_visibility: editOrder.reference_visibility || "all",
  } : {
    title: "", description: "", task_category: "temizlik", task_type: "",
    priority: "normal", order_type: "zorunlu_uygula", operation_effect: "",
    requires_approval: false, is_collective: false, source: "",
    location_kind: "park", location_ref_id: "", location_label: "",
    location_lat: null, location_lng: null, locations: [],
    scheduled_date: todayISO(), start_time: "", end_time: "", due_date: "",
    need_start_photo: false, need_end_photo: false,
    need_start_video: false, need_end_video: false,
    need_start_gps: false, need_end_gps: false,
    leader_personnel_id: "",
    reference_media: [], reference_visibility: "all",
  });
  const [selectedPeople, setSelectedPeople] = useState(() => editOrder
    ? (editOrder.assignees || [])
        .filter(a => a.assignee_kind === "person" && a.personnel_id)
        .map(a => ({ id: a.personnel_id, name: a.personnel_name || "Personel" }))
    : []); // [{id,name}]
  // Görev sorumlusu formen(ler) — toplu çalışmada birden çok seçilebilir.
  const [gorevSorumluIds, setGorevSorumluIds] = useState(() => {
    if (!editOrder) return new Set();
    const lid = editOrder.leader_personnel_id;
    return new Set((editOrder.assignees || [])
      .filter(a => a.assignee_kind === "person" && a.personnel_id && a.personnel_role &&
        rankOfRole(a.personnel_role) === ROLE_RANK.formen &&
        ((a.must_provide_start_evidence || a.must_provide_end_evidence) || (lid && a.personnel_id === lid)))
      .map(a => a.personnel_id));
  });
  // Kademeli filtre (Personel sekmesi gibi): MY → BM → Formen
  const [fMY, setFMY] = useState("all");
  const [fBM, setFBM] = useState("all");
  const [fFormen, setFFormen] = useState("all");
  const subById = useMemo(() => {
    const m = new Map();
    (subordinates || []).forEach(p => { if (p && p.id) m.set(p.id, p); });
    // Edit/yeniden gönder: emirdeki atanan artık senin alt zincirinde olmasa da (reorg)
    // rolü emir kaydından tamamla — yoksa roleOf boş döner, formen sayılmaz ve
    // "görev sorumlusu" doğrulaması + yönetici/bilgi bandı yanlış olur.
    (editOrder?.assignees || []).forEach(a => {
      if (a.assignee_kind === "person" && a.personnel_id && a.personnel_role && !m.has(a.personnel_id)) {
        m.set(a.personnel_id, { id: a.personnel_id, name: a.personnel_name || "Personel", role: a.personnel_role });
      }
    });
    return m;
  }, [subordinates, editOrder]);
  // Görev sorumlusu seti, seçili formenlere göre otomatik düzeltilir: tek formen → otomatik
  // sorumlu; çoklu formende geçersiz (artık seçili olmayan) sorumlular ayıklanır.
  useEffect(() => {
    const formenIds = selectedPeople.filter(p => roleOf(subById.get(p.id)) === "formen").map(p => p.id);
    setGorevSorumluIds(prev => {
      if (formenIds.length === 1) return new Set(formenIds);
      const next = new Set([...prev].filter(id => formenIds.includes(id)));
      return next.size === prev.size && [...next].every(id => prev.has(id)) ? prev : next;
    });
  }, [selectedPeople, subById]);
  // Bir kişinin hiyerarşi zinciri (App.resolveHierarchyIds mantığı)
  const chainOf = (p) => {
    const byId = (id) => subById.get(id);
    const r = roleOf(p);
    let myId = p.mudur_yardimcisi_id || null;
    let bmId = p.bolge_muhendisi_id || null;
    let formenId = p.formen_id || null;
    if (r === "mudur_yardimcisi") myId = p.id;
    else if (r === "bolge_muhendisi") { bmId = p.id; if (!myId) myId = byId(p.mudur_yardimcisi_id)?.id || myId; }
    else if (r === "formen") { formenId = p.id; if (!myId && bmId) myId = byId(bmId)?.mudur_yardimcisi_id || myId; }
    else if (r === "park_gorevlisi") {
      if (!bmId && formenId) bmId = byId(formenId)?.bolge_muhendisi_id || bmId;
      if (!myId && bmId) myId = byId(bmId)?.mudur_yardimcisi_id || myId;
    }
    return { myId, bmId, formenId };
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Seçili tarihte izinli/raporlu olanlar — seçilemez (gri) gösterilir
  useEffect(() => {
    const d = form.scheduled_date;
    if (!d) { setUnavail(new Map()); return; }
    let alive = true;
    axios.get(`${apiBase}/unavailable`, { params: { date: d } })
      .then(r => { if (alive) setUnavail(new Map((Array.isArray(r.data) ? r.data : []).map(x => [x.personnel_id, x]))); })
      .catch(() => {});
    return () => { alive = false; };
  }, [apiBase, form.scheduled_date]);

  // Kademeli seçenekler: MY → BM (MY'ye bağlı) → Formen (BM'e bağlı)
  const myFilterOptions = useMemo(
    () => (subordinates || []).filter(p => roleOf(p) === "mudur_yardimcisi").map(p => ({ id: p.id, name: p.name })),
    [subordinates]
  );
  const bmFilterOptions = useMemo(
    () => (subordinates || []).filter(p => roleOf(p) === "bolge_muhendisi" && (fMY === "all" || p.mudur_yardimcisi_id === fMY)).map(p => ({ id: p.id, name: p.name })),
    [subordinates, fMY]
  );
  const formenFilterOptions = useMemo(
    () => (subordinates || []).filter(p => roleOf(p) === "formen" && (fBM === "all" || p.bolge_muhendisi_id === fBM) && (fMY === "all" || chainOf(p).myId === fMY)).map(p => ({ id: p.id, name: p.name })),
    [subordinates, fBM, fMY] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Üst seçim değişince geçersiz alt seçimi sıfırla (kademeli daralma)
  useEffect(() => { if (fBM !== "all" && !bmFilterOptions.some(o => o.id === fBM)) setFBM("all"); }, [fBM, bmFilterOptions]);
  useEffect(() => { if (fFormen !== "all" && !formenFilterOptions.some(o => o.id === fFormen)) setFFormen("all"); }, [fFormen, formenFilterOptions]);

  // Kademeli filtreye göre adaylar — seçilen düğüm + altındakiler
  const candidates = useMemo(() => {
    return (subordinates || []).filter(p => {
      const c = chainOf(p);
      if (fMY !== "all" && c.myId !== fMY && p.id !== fMY) return false;
      if (fBM !== "all" && c.bmId !== fBM && p.id !== fBM) return false;
      if (fFormen !== "all" && c.formenId !== fFormen && p.id !== fFormen) return false;
      return true;
    });
  }, [subordinates, fMY, fBM, fFormen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Eklenen konumların sorumlu personeli — KONUM TÜRÜNE GÖRE gruplu (park / pasif alan /
  // protokollü park / serbest işaret ayrı ayrı). Doğrudan atanan (formen/PG) + üst zinciri (BM, MY).
  const ownersByKind = useMemo(() => {
    const byId = new Map((subordinates || []).map(p => [p.id, p]));
    const chainOf = (pid, acc) => {
      if (!pid) return;
      acc.add(pid);
      const p = byId.get(pid);
      if (p) {
        if (p.formen_id) acc.add(p.formen_id);
        if (p.bolge_muhendisi_id) acc.add(p.bolge_muhendisi_id);
        if (p.mudur_yardimcisi_id) acc.add(p.mudur_yardimcisi_id);
      }
    };
    const itemOwners = (item, acc) => {
      if (!item) return;
      (item.assigned_personnel_ids || []).forEach(pid => chainOf(pid, acc));
      chainOf(item.assigned_formen_id, acc);
      const bm = item.bolge_muhendisi_id || item.assigned_bolge_muhendisi_id;
      const my = item.mudur_yardimcisi_id || item.assigned_mudur_yardimcisi_id;
      if (bm) acc.add(bm);
      if (my) acc.add(my);
    };
    const allItems = [...(parks || []), ...(passiveAreas || []), ...(protokolParks || [])];
    const result = {}; // kind -> Set(ids)
    (form.locations || []).forEach(l => {
      const acc = result[l.kind] || new Set();
      if (l.ref_id) {
        itemOwners(allItems.find(x => x.id === l.ref_id), acc);
      } else {
        // Serbest işaret: nokta poligon içindeyse o kayıt; değilse en yakın kayıt (bölgeyi temsilen)
        const c = Array.isArray(l.pin) ? l.pin : (l.lat != null && l.lng != null ? [Number(l.lat), Number(l.lng)] : null);
        if (c) {
          let hit = allItems.find(x => Array.isArray(x.boundaries) && x.boundaries.length >= 3 && pointInPolygon(c, x.boundaries));
          if (!hit) {
            let bestD = Infinity;
            for (const x of allItems) {
              const lat = x.latitude != null ? Number(x.latitude) : null;
              const lng = x.longitude != null ? Number(x.longitude) : null;
              if (lat == null || lng == null) continue;
              const d = (lat - c[0]) ** 2 + (lng - c[1]) ** 2;
              if (d < bestD) { bestD = d; hit = x; }
            }
          }
          itemOwners(hit, acc);
        }
      }
      if (acc.size) result[l.kind] = acc;
    });
    return result;
  }, [form.locations, parks, passiveAreas, protokolParks, subordinates]);

  // Tüm sorumlular (⭐ işaretleme için birleşik küme)
  const parkOwnerIds = useMemo(() => {
    const s = new Set();
    Object.values(ownersByKind).forEach(set => set.forEach(id => s.add(id)));
    return s;
  }, [ownersByKind]);

  // Banner için: tür → hiyerarşik sıralı sorumlu listesi
  const ownerGroups = useMemo(
    () => Object.entries(ownersByKind).map(([kind, idSet]) => ({
      kind,
      owners: (subordinates || []).filter(p => idSet.has(p.id))
        .sort((a, b) => roleRank(a) - roleRank(b) || String(a.name || "").localeCompare(String(b.name || ""), "tr")),
    })).filter(g => g.owners.length > 0),
    [ownersByKind, subordinates]
  );

  const removePerson = (id) => setSelectedPeople(prev => prev.filter(x => x.id !== id));
  const clearPeople = () => { setSelectedPeople([]); setForm(f => ({ ...f, leader_personnel_id: "" })); };
  const isSelected = (id) => selectedPeople.some(x => x.id === id);
  const togglePerson = (p) => {
    if (unavail.has(p.id)) { const z = unavail.get(p.id); toast.error(`Bu personel ${z?.reason_type || "izinli"} (seçilemez)`); return; }
    setSelectedPeople(prev => prev.some(x => x.id === p.id) ? prev.filter(x => x.id !== p.id) : [...prev, { id: p.id, name: p.name }]);
  };
  const toggleAll = (people) => {
    const sel = (people || []).filter(m => !unavail.has(m.id));
    const allSel = sel.length > 0 && sel.every(m => isSelected(m.id));
    if (allSel) { const ids = new Set(sel.map(m => m.id)); setSelectedPeople(prev => prev.filter(x => !ids.has(x.id))); }
    else { setSelectedPeople(prev => { const have = new Set(prev.map(x => x.id)); return [...prev, ...sel.filter(m => !have.has(m.id)).map(m => ({ id: m.id, name: m.name }))]; }); }
  };
  // Seçili kişileri role göre grupla (kademeli daraltma için)
  const selByRole = useMemo(() => {
    const r = { mudur_yardimcisi: new Set(), bolge_muhendisi: new Set(), formen: new Set(), park_gorevlisi: new Set() };
    selectedPeople.forEach(sp => { const p = subById.get(sp.id); if (p) { const role = roleOf(p); if (r[role]) r[role].add(sp.id); } });
    return r;
  }, [selectedPeople, subById]);

  // Role göre dropdown grupları (MY → BM → Formen → PG). Üst seviyeden seçim varsa alt seviye DARALIR;
  // ANCAK zaten seçili olanlar her zaman listede kalır (farklı dalda seçilenler gizlenmesin).
  const pickerRoleGroups = useMemo(() => {
    const sortName = (arr) => arr.slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "tr"));
    const byRole = new Map();
    (subordinates || []).forEach(p => { const r = roleOf(p); if (!r) return; if (!byRole.has(r)) byRole.set(r, []); byRole.get(r).push(p); });
    const myAll = byRole.get("mudur_yardimcisi") || [];
    const bmAll = byRole.get("bolge_muhendisi") || [];
    const formenAll = byRole.get("formen") || [];
    const pgAll = byRole.get("park_gorevlisi") || [];
    const { mudur_yardimcisi: selMY, bolge_muhendisi: selBM, formen: selFormen, park_gorevlisi: selPG } = selByRole;
    const bm = bmAll.filter(p => selBM.has(p.id) || (!selMY.size || (p.mudur_yardimcisi_id && selMY.has(p.mudur_yardimcisi_id))));
    const formen = formenAll.filter(p => {
      if (selFormen.has(p.id)) return true; // zaten seçili → her zaman göster
      if (selBM.size) return p.bolge_muhendisi_id && selBM.has(p.bolge_muhendisi_id);
      if (selMY.size) { const c = chainOf(p); return c.myId && selMY.has(c.myId); }
      return true;
    });
    const pg = pgAll.filter(p => {
      if (selPG.has(p.id)) return true;
      if (selFormen.size) return p.formen_id && selFormen.has(p.formen_id);
      if (selBM.size) { const c = chainOf(p); return c.bmId && selBM.has(c.bmId); }
      return true;
    });
    const out = [];
    if (myAll.length) out.push({ role: "mudur_yardimcisi", label: roleLabel("mudur_yardimcisi"), people: sortName(myAll) });
    if (bm.length) out.push({ role: "bolge_muhendisi", label: roleLabel("bolge_muhendisi"), people: sortName(bm) });
    if (formen.length) out.push({ role: "formen", label: roleLabel("formen"), people: sortName(formen) });
    if (pg.length) out.push({ role: "park_gorevlisi", label: roleLabel("park_gorevlisi"), people: sortName(pg) });
    return out;
  }, [subordinates, selByRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // Referans görsel yükleme (mevcut /api/upload-revision-photo altyapısı)
  const uploadRefFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingRef(true);
    try {
      const added = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await axios.post(`${API}/api/upload-revision-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (r.data && r.data.url) added.push({ url: r.data.url, type: "photo", name: file.name });
      }
      if (added.length) {
        setForm(f => ({ ...f, reference_media: [...f.reference_media, ...added] }));
        toast.success(`${added.length} referans görsel eklendi`);
      }
    } catch (e) {
      toast.error("Görsel yüklenemedi");
    } finally {
      setUploadingRef(false);
    }
  };
  const removeRef = (idx) => setForm(f => ({ ...f, reference_media: f.reference_media.filter((_, i) => i !== idx) }));

  // Seçili konum türüne göre kayıtlı liste
  const regList = form.location_kind === "park" ? parks
    : form.location_kind === "pasif_alan" ? passiveAreas
    : form.location_kind === "protokol" ? protokolParks : [];
  const regPlaceholder = form.location_kind === "pasif_alan" ? "Pasif alan seçin"
    : form.location_kind === "protokol" ? "Protokollü park seçin" : "Park seçin";

  // Serbest işaret: adres/yer arama (backend /api/geocode)
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [mapLayer, setMapLayer] = useState("street"); // street | satellite
  const [pinTargetIdx, setPinTargetIdx] = useState(null); // hangi kayıtlı konuma tam-nokta konuyor
  const [multiPin, setMultiPin] = useState(null); // {refId,kind,label,boundaries} — aynı parka çoklu nokta
  const [drawArea, setDrawArea] = useState(false); // serbest: belirli bir alanın sınırını çizme modu
  const [drawPoints, setDrawPoints] = useState([]); // çizilmekte olan serbest alan köşeleri
  const areaSeqRef = useRef(0); // serbest alanlara benzersiz uid (içine nokta işaretlemeyi gruplamak için)
  const doGeocode = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    try {
      const r = await axios.get(`${API}/api/geocode`, { params: { address: searchQ.trim() } });
      if (r.data && r.data.success) {
        setForm(f => ({ ...f, location_lat: Number(r.data.latitude), location_lng: Number(r.data.longitude) }));
        toast.success(`Bulundu: ${r.data.display_name || ""}`);
      } else {
        toast.error("Yer bulunamadı");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Arama başarısız");
    } finally {
      setSearching(false);
    }
  };

  // Konum ekleme/çıkarma (çoklu)
  const addRegisteredLocation = (id) => {
    if (!id) return;
    const item = (regList || []).find(x => x.id === id);
    if (!item) return;
    if (form.locations.some(l => l.ref_id === id)) { toast.info("Bu konum zaten ekli"); return; }
    setForm(f => ({
      ...f,
      location_ref_id: "",
      locations: [...f.locations, {
        kind: f.location_kind, ref_id: id,
        label: item.name || item.park_name || "",
        lat: item.latitude != null ? Number(item.latitude) : null,
        lng: item.longitude != null ? Number(item.longitude) : null,
        boundaries: Array.isArray(item.boundaries) ? item.boundaries : null,
      }],
    }));
  };
  const addFreeLocation = () => {
    if (form.location_lat == null || form.location_lng == null) { toast.error("Önce haritada nokta seçin"); return; }
    const lat = Number(form.location_lat), lng = Number(form.location_lng);
    setForm(f => ({
      ...f,
      location_lat: null, location_lng: null,
      locations: [...f.locations, { kind: "serbest", ref_id: null, lat, lng, label: `${lat.toFixed(5)}, ${lng.toFixed(5)}` }],
    }));
  };
  // Serbest alan çizimi: çizilen köşelerden bir poligon konumu ekler
  const finishFreeArea = () => {
    if (drawPoints.length < 3) { toast.error("Alan için en az 3 köşe gerekli."); return; }
    const pts = drawPoints;
    // Merkez (centroid) — etiket/odak için
    const cy = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const cx = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    const uid = `area-${++areaSeqRef.current}`;
    setForm(f => ({
      ...f,
      location_lat: null, location_lng: null,
      locations: [...f.locations, {
        kind: "serbest", ref_id: null, uid,
        lat: cy, lng: cx,
        boundaries: pts,
        label: `Çizili alan (${pts.length} köşe)`,
      }],
    }));
    setDrawPoints([]);
    setDrawArea(false);
    toast.success(`✅ Alan eklendi (${pts.length} köşe). İçine nokta işaretlemek için ⊕ simgesine basın.`);
  };
  const cancelFreeArea = () => { setDrawPoints([]); setDrawArea(false); };
  const removeLocation = (idx) => setForm(f => ({ ...f, locations: f.locations.filter((_, i) => i !== idx) }));
  // İşaretli noktaya/konuma açıklama notu (ör. "kurumuş ağaç", "su patlağı")
  const setLocationNote = (idx, val) => setForm(f => ({ ...f, locations: f.locations.map((l, i) => i === idx ? { ...l, note: val } : l) }));
  const locKindLabel = (kind) => (LOCATION_KINDS_UI.find(k => k.value === kind) || {}).label || kind;

  // Pinli konum = kırmızı imleç (tam nokta); pinsiz = varsayılan marker (merkez)
  const locMarkers = form.locations.filter(l => !Array.isArray(l.pin) && l.lat != null && l.lng != null).map(l => [Number(l.lat), Number(l.lng)]);
  const locPins = form.locations.filter(l => Array.isArray(l.pin)).map(l => l.pin);
  const locPolygons = form.locations.filter(l => Array.isArray(l.boundaries) && l.boundaries.length > 2).map(l => l.boundaries);
  const pendingPoint = form.location_kind === "serbest" && form.location_lat != null && form.location_lng != null
    ? [Number(form.location_lat), Number(form.location_lng)] : null;
  const activeCenter = pendingPoint
    || (locPins.length ? locPins[locPins.length - 1] : null)
    || (locMarkers.length ? locMarkers[locMarkers.length - 1] : null)
    || (locPolygons[0] && locPolygons[0][0])
    || mapCenter;

  // Harita tıklaması: çoklu-nokta modundaysa aynı parka YENİ nokta satırı ekler
  // (mod açık kalır, peş peşe birden çok ağaç/nokta işaretlenebilir), pin modunda
  // o konuma tam-nokta koyar, serbest modda bekleyen nokta belirler.
  const onMapPick = (lat, lng) => {
    if (drawArea) {
      setDrawPoints(pts => [...pts, [lat, lng]]);
      return;
    }
    if (multiPin != null) {
      setForm(f => {
        const gid = multiPin.uid || multiPin.refId;
        const n = f.locations.filter(l => (l.uid || l.ref_id) === gid && Array.isArray(l.pin)).length + 1;
        const isSerbest = multiPin.kind === "serbest";
        return {
          ...f,
          locations: [...f.locations, {
            kind: multiPin.kind, ref_id: multiPin.refId, uid: multiPin.uid || null,
            label: `${multiPin.label} · Nokta ${n}`,
            // Serbest çocuk nokta backend için lat/lng ister; kayıtlı için pin yeterli.
            // boundaries=null: poligon yalnızca ana alanda çizilsin (mükerrer çizim olmasın).
            lat: isSerbest ? lat : null, lng: isSerbest ? lng : null, pin: [lat, lng],
            boundaries: null,
          }],
        };
      });
      return; // mod açık kalır — birden fazla nokta eklenebilir
    }
    if (pinTargetIdx != null) {
      setForm(f => ({ ...f, locations: f.locations.map((l, i) => i === pinTargetIdx ? { ...l, pin: [lat, lng] } : l) }));
      setPinTargetIdx(null);
    } else if (form.location_kind === "serbest") {
      setForm(f => ({ ...f, location_lat: lat, location_lng: lng }));
    }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Başlık zorunlu"); return; }
    if (selectedPeople.length === 0) { toast.error("En az bir görevli seçin"); return; }
    if (form.order_type !== "bilgilendirme" && !form.scheduled_date) {
      toast.error("Tarih zorunlu (bilgilendirme hariç)"); return;
    }
    // Görev sorumlusu = FORMEN(ler). Tek formen → otomatik; 2+ formen → en az biri seçilmeli (çoklu olabilir).
    const formenSel = selectedPeople.filter(p => roleOf(subById.get(p.id)) === "formen");
    const gsIds = formenSel.length === 1 ? new Set([formenSel[0].id]) : gorevSorumluIds;
    if (formenSel.length >= 2 && gsIds.size === 0) {
      toast.error("Birden fazla formen seçtin — en az bir görev sorumlusu seç (istersen birden çok).");
      return;
    }
    // İlk görev sorumlusu = primary (leader_personnel_id, geriye uyumluluk). BM/MY asla sorumlu olmaz.
    const leaderId = [...gsIds][0] || null;
    // Görev sorumlusu formen(ler) kanıt sorumlusudur (kanıt istenmese de çapa olur — must_provide).
    const isEvidenceOwner = (p) => gsIds.has(p.id);
    const payload = {
      title: form.title.trim(),
      description: form.description || null,
      task_category: form.task_category,
      task_type: form.task_type || null,
      priority: form.priority,
      order_type: form.order_type,
      operation_effect: form.operation_effect || null,
      requires_approval: !!form.requires_approval,
      is_collective: !!form.is_collective,
      source: form.source || null,
      leader_personnel_id: leaderId,
      locations: form.locations.map(l => ({
        kind: l.kind, ref_id: l.ref_id || null,
        lat: l.lat ?? null, lng: l.lng ?? null, label: l.label || null,
        pin_lat: Array.isArray(l.pin) ? l.pin[0] : null,
        pin_lng: Array.isArray(l.pin) ? l.pin[1] : null,
        boundaries: Array.isArray(l.boundaries) && l.boundaries.length > 2 ? l.boundaries : null,
        note: (l.note && l.note.trim()) ? l.note.trim() : null,
      })),
      scheduled_date: form.scheduled_date || null,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      due_date: form.due_date || null,
      need_start_photo: form.need_start_photo,
      need_end_photo: form.need_end_photo,
      need_start_video: form.need_start_video,
      need_end_video: form.need_end_video,
      need_start_gps: form.need_start_gps,
      need_end_gps: form.need_end_gps,
      reference_media: form.reference_media,
      reference_visibility: form.reference_visibility,
      assignees: selectedPeople.map(p => ({
        kind: "person",
        personnel_id: p.id,
        must_provide_start_evidence: isEvidenceOwner(p),
        must_provide_end_evidence: isEvidenceOwner(p),
      })),
    };
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${apiBase}/${editOrder.id}`, payload);
        toast.success("Emir düzenlendi ve yeniden gönderildi");
      } else {
        await axios.post(apiBase, payload);
        toast.success("Görev oluşturuldu");
      }
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.error?.message || (isEdit ? "Gönderilemedi" : "Oluşturulamadı");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DraggableModal
      isOpen
      onClose={onClose}
      title={isEdit ? "Düzenle ve Yeniden Gönder" : "Anlık Yeni Görev"}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>İptal</Button>
          <Button data-testid="op-submit-btn" onClick={submit} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? "Kaydediliyor..." : (isEdit ? "Yeniden Gönder" : "Oluştur")}
          </Button>
        </div>
      }
    >
        <div className="space-y-3">
          <div>
            <Label>Başlık *</Label>
            <Input data-testid="op-title-input" value={form.title} onChange={e => set("title", e.target.value)} placeholder="örn. Kuğulu Park çöp toplama" />
          </div>
          <div>
            <Label>Açıklama</Label>
            <Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} />
          </div>

          {/* Toplu Çalışma modu — iOS tarzı aç/kapa anahtarı */}
          <div className={`flex items-center justify-between rounded-md border px-3 py-2 ${form.is_collective ? "border-green-300 bg-green-50" : "border-gray-200"}`}>
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-green-600" /> Toplu Çalışma
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {form.is_collective ? "Açık — birden çok formen + ekip" : "Kapalı — tek ekip"}
              </div>
            </div>
            <button type="button" role="switch" aria-checked={form.is_collective}
              onClick={() => setForm(f => ({ ...f, is_collective: !f.is_collective }))}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${form.is_collective ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${form.is_collective ? "translate-x-5" : ""}`} />
            </button>
          </div>
          {form.is_collective && (
            <p className="text-[11px] text-green-700 -mt-1">
              Birden çok formen ekle (kendi altındakilerden). Her formen, emri kendi panelinde açıp ekibini (Çim/Elektrik vb.) ekler. Aynı operasyona birden çok konum eklenebilir.
            </p>
          )}

          {canUploadRef && (
            <div>
              <Label>Referans Foto (opsiyonel)</Label>
              <p className="text-[11px] text-gray-400 mb-1">Göreve örnek/brief görsel ekleyin — kanıttan farklı, görevli ayrıca kendi kanıtını yükler.</p>
              <input type="file" accept="image/*" multiple disabled={uploadingRef}
                onChange={e => { uploadRefFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
                className="text-sm" />
              {uploadingRef && <span className="text-xs text-gray-500 ml-2">Yükleniyor...</span>}
              {form.reference_media.length > 0 && (
                <>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {form.reference_media.map((m, idx) => (
                      <div key={idx} className="relative">
                        <img src={mediaUrl(API, m.url)} alt={m.name || "referans"} className="h-16 w-full object-cover rounded border" />
                        <button type="button" onClick={() => removeRef(idx)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <Label>Bu görselleri kimler görsün?</Label>
                    <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.reference_visibility}
                      onChange={e => set("reference_visibility", e.target.value)}>
                      <option value="all">Herkes (görevliler dahil)</option>
                      <option value="managers">Sadece yöneticiler</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Görev Kategorisi</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.task_category}
                      onChange={e => { set("task_category", e.target.value); set("task_type", ""); }}>
                {TASK_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Görev Türü</Label>
              {form.task_category === "serbest" ? (
                <Input value={form.task_type} onChange={e => set("task_type", e.target.value)} placeholder="Serbest açıklama" />
              ) : (
                <SearchableSelect
                  value={form.task_type}
                  onChange={(name) => {
                    const opt = ALL_TASK_TYPES.find(o => o.id === name);
                    setForm(f => ({ ...f, task_type: opt ? opt.id : "", task_category: opt ? opt.cat : f.task_category }));
                  }}
                  options={ALL_TASK_TYPES}
                  showAllOption={false}
                  placeholder="Görev türü seçin"
                  searchPlaceholder="Tür ara (çim, budama, sulama...)"
                  emptyText="Tür bulunamadı."
                  className="h-9"
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Öncelik</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.priority}
                      onChange={e => set("priority", e.target.value)}>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Emir Türü</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.order_type}
                      onChange={e => set("order_type", e.target.value)}>
                {ORDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Operasyon Etkisi</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.operation_effect}
                      onChange={e => set("operation_effect", e.target.value)}>
                {OPERATION_EFFECTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Kaynak</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.source}
                      onChange={e => set("source", e.target.value)}>
                {SOURCES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {/* Konum — ÇOKLU (park / pasif alan / protokollü park / serbest işaret) */}
          <div>
            <Label>Konum(lar) — birden fazla eklenebilir</Label>
            <div className="grid grid-cols-4 gap-1 mt-1">
              {LOCATION_KINDS_UI.map(k => (
                <button key={k.value} type="button"
                  onClick={() => setForm(f => ({ ...f, location_kind: k.value, location_ref_id: "", location_lat: null, location_lng: null }))}
                  className={`text-xs rounded-md border px-2 py-1.5 transition-colors ${form.location_kind === k.value ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                  {k.label}
                </button>
              ))}
            </div>

            {form.location_kind !== "serbest" && (
              <div className="mt-2">
                <SearchableSelect
                  value={form.location_ref_id}
                  onChange={(id) => addRegisteredLocation(id)}
                  options={(regList || []).filter(x => !form.locations.some(l => l.ref_id === x.id)).map(x => ({ id: x.id, name: x.name || x.park_name }))}
                  showAllOption={false}
                  placeholder={`${regPlaceholder} — seçince listeye eklenir`}
                  searchPlaceholder="Ara..."
                  emptyText="Kayıt bulunamadı."
                  className="h-9"
                />
              </div>
            )}

            {form.location_kind === "serbest" && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-2">
                  <Input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); doGeocode(); } }}
                    placeholder="Adres/yer ara (örn. 1453. Cadde Çankaya)" disabled={drawArea} />
                  <Button type="button" variant="outline" onClick={doGeocode} disabled={searching || drawArea}>
                    <Search className="h-4 w-4 mr-1" />{searching ? "..." : "Ara"}
                  </Button>
                  <Button type="button" onClick={addFreeLocation} disabled={pendingPoint == null || drawArea} className="bg-green-600 hover:bg-green-700">
                    <Plus className="h-4 w-4 mr-1" />Ekle
                  </Button>
                </div>
                {/* Belirli bir alanın sınırını çiz (nokta yerine poligon) */}
                <div className="flex gap-2 items-center">
                  {!drawArea ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => { setDrawArea(true); setDrawPoints([]); setForm(f => ({ ...f, location_lat: null, location_lng: null })); }}>
                      🔷 Alan Çiz
                    </Button>
                  ) : (
                    <>
                      <Button type="button" size="sm" className="bg-green-600 hover:bg-green-700" disabled={drawPoints.length < 3} onClick={finishFreeArea}>
                        ✅ Çizimi Tamamla{drawPoints.length >= 3 ? ` (${drawPoints.length})` : ""}
                      </Button>
                      {drawPoints.length > 0 && (
                        <Button type="button" variant="outline" size="sm" onClick={() => setDrawPoints(pts => pts.slice(0, -1))}>↶ Son köşeyi sil</Button>
                      )}
                      <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={cancelFreeArea}>Vazgeç</Button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Eklenen konumlar — her birine açıklama notu yazılabilir */}
            <div className="mt-2 space-y-1.5">
              {form.locations.length === 0 && <span className="text-xs text-gray-400">Henüz konum eklenmedi.</span>}
              {form.locations.map((l, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 text-xs">
                    <MapPin className="h-3 w-3" />
                    <span className="text-emerald-500">{locKindLabel(l.kind)}:</span>{" "}
                    {(() => {
                      const c = Array.isArray(l.pin) ? l.pin : (l.lat != null && l.lng != null ? [Number(l.lat), Number(l.lng)] : null);
                      return c
                        ? <a href={mapsDirUrl(c[0], c[1])} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline" title="Yol tarifi al ve git">{l.label || "konum"}</a>
                        : <span>{l.label || "konum"}</span>;
                    })()}
                    {Array.isArray(l.pin) && <span className="text-red-600" title="işaretli nokta">●</span>}
                    {((l.kind !== "serbest") || (Array.isArray(l.boundaries) && l.boundaries.length > 2)) && !Array.isArray(l.pin) && (
                      <button type="button"
                        onClick={() => setMultiPin({ refId: l.ref_id, uid: l.uid || null, kind: l.kind, label: l.label || "alan", boundaries: l.boundaries })}
                        title="Bu alanda birden fazla nokta işaretle (ör. 3 kurumuş ağaç)"
                        className="text-gray-400 hover:text-red-600">
                        <Crosshair className="h-3 w-3" />
                      </button>
                    )}
                    <button type="button" onClick={() => removeLocation(idx)} className="hover:text-red-600"><X className="h-3 w-3" /></button>
                  </span>
                  {/* Not yalnızca işaretli NOKTALARDA: alan içine konan pin'ler ve
                      serbest tekil noktalar. Çizili alan (boundaries olan serbest) ve
                      kayıtlı park/alan seçiminde gösterilmez. */}
                  {(Array.isArray(l.pin) || (l.kind === "serbest" && !(Array.isArray(l.boundaries) && l.boundaries.length > 2))) && (
                    <Input
                      value={l.note || ""}
                      onChange={e => setLocationNote(idx, e.target.value)}
                      placeholder="Not (örn. kurumuş ağaç, su patlağı)"
                      className="h-7 text-xs flex-1 min-w-[150px]"
                    />
                  )}
                </div>
              ))}
            </div>

            {multiPin != null && (
              <div className="mt-2 text-xs text-red-700 flex items-center gap-2 bg-red-50 border border-red-200 rounded px-2 py-1">
                <Crosshair className="h-3 w-3 flex-shrink-0" />
                "{multiPin.label}" içinde her ağaç/nokta için haritaya çift tıklayın — birden fazla nokta ekleyebilirsiniz.
                <button type="button" onClick={() => setMultiPin(null)} className="underline ml-auto font-medium">Bitti</button>
              </div>
            )}

            {/* Harita — tüm eklenen konumlar + bekleyen serbest nokta */}
            <div className="mt-2">
              <div className="flex justify-end gap-1 mb-1">
                {Object.entries(TILE_LAYERS).map(([key, cfg]) => (
                  <button key={key} type="button" onClick={() => setMapLayer(key)}
                    className={`text-xs rounded-md border px-2 py-1 ${mapLayer === key ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                    {cfg.label}
                  </button>
                ))}
              </div>
              <div className="h-96 rounded-md overflow-hidden border">
                <MapContainer center={activeCenter} zoom={13} doubleClickZoom={false} style={{ height: "100%", width: "100%" }}>
                  <TileLayer key={mapLayer} url={TILE_LAYERS[mapLayer].url} attribution={TILE_LAYERS[mapLayer].attribution} />
                  <RecenterMap center={activeCenter} />
                  {(form.location_kind === "serbest" || pinTargetIdx != null || multiPin != null || drawArea) && (
                    <ClickToMark onPick={onMapPick} single={drawArea} />
                  )}
                  {locMarkers.map((pos, i) => <Marker key={`m${i}`} position={pos} />)}
                  {locPins.map((pos, i) => (
                    <CircleMarker key={`pin${i}`} center={pos} radius={8}
                      pathOptions={{ color: "#86198f", fillColor: "#d946ef", fillOpacity: 0.9 }} />
                  ))}
                  {pendingPoint && !drawArea && <Marker position={pendingPoint} />}
                  {locPolygons.map((poly, i) => <Polygon key={`p${i}`} positions={poly} pathOptions={{ color: "#16a34a" }} />)}
                  {/* Çizilmekte olan serbest alan: poligon + köşe daireleri */}
                  {drawArea && drawPoints.length >= 2 && (
                    <Polygon positions={drawPoints} pathOptions={{ color: "#7c3aed", fillColor: "#a78bfa", fillOpacity: 0.25, weight: 2, dashArray: "4" }} />
                  )}
                  {drawArea && drawPoints.map((p, i) => (
                    <CircleMarker key={`dp${i}`} center={p} radius={5}
                      pathOptions={{ color: "#5b21b6", fillColor: "#7c3aed", fillOpacity: 1, weight: 2 }}>
                      <LeafletTooltip direction="top" offset={[0, -6]}>{i + 1}. köşe</LeafletTooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
              {form.location_kind === "serbest" && (
                drawArea
                  ? <p className="text-[11px] text-purple-600">Alan çizimi: haritaya tek tıklayarak köşeleri ekleyin (en az 3), sonra "Çizimi Tamamla".</p>
                  : pendingPoint
                    ? <p className="text-[11px] text-gray-500">Bekleyen nokta: {pendingPoint[0].toFixed(5)}, {pendingPoint[1].toFixed(5)} — "Ekle" ile listeye alın.</p>
                    : <p className="text-[11px] text-gray-400">Adres aratıp ya da haritaya çift tıklayarak nokta seçin. Belirli bir alan için "Alan Çiz".</p>
              )}
            </div>
          </div>

          {/* Zamanlama */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tarih{form.order_type !== "bilgilendirme" ? " *" : ""}</Label>
              <Input type="date" value={form.scheduled_date} onChange={e => set("scheduled_date", e.target.value)} />
            </div>
            <div>
              <Label>Son Tarih</Label>
              <Input type="date" value={form.due_date} onChange={e => set("due_date", e.target.value)} />
            </div>
            <div>
              <Label>Başlangıç Saati</Label>
              <Input type="time" value={form.start_time} onChange={e => set("start_time", e.target.value)} />
            </div>
            <div>
              <Label>Bitiş Saati</Label>
              <Input type="time" value={form.end_time} onChange={e => set("end_time", e.target.value)} />
            </div>
          </div>

          {/* Kanıt istekleri */}
          <div>
            <Label>İstenen Kanıtlar</Label>
            <div className="grid grid-cols-2 gap-1 mt-1 text-sm">
              <Chk label="Başlangıç fotoğrafı" v={form.need_start_photo} on={() => set("need_start_photo", !form.need_start_photo)} />
              <Chk label="Bitiş fotoğrafı" v={form.need_end_photo} on={() => set("need_end_photo", !form.need_end_photo)} />
              <Chk label="Başlangıç videosu" v={form.need_start_video} on={() => set("need_start_video", !form.need_start_video)} />
              <Chk label="Bitiş videosu" v={form.need_end_video} on={() => set("need_end_video", !form.need_end_video)} />
              <Chk label="Başlangıç GPS" v={form.need_start_gps} on={() => set("need_start_gps", !form.need_start_gps)} />
              <Chk label="Bitiş GPS" v={form.need_end_gps} on={() => set("need_end_gps", !form.need_end_gps)} />
            </div>
          </div>

          <Chk label="Tamamlanınca onay gereksin" v={form.requires_approval} on={() => set("requires_approval", !form.requires_approval)} />

          {/* Görevliler */}
          <div>
            <Label>Görevliler * (alt hiyerarşinizdeki personel)</Label>
            {ownerGroups.length > 0 && (
              <div className="mt-1 mb-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 space-y-0.5">
                {ownerGroups.map(g => (
                  <div key={g.kind}>
                    <span className="font-medium">📍 {locKindLabel(g.kind)} sorumluları:</span>{" "}
                    {g.owners.map(p => `${p.name} (${roleLabel(roleOf(p))})`).join(", ")}
                  </div>
                ))}
                <span className="block text-[11px] text-amber-600 mt-0.5">⭐ ile işaretliler bu alanlara en yakın sorumlulardır; isterseniz başkasını da görevlendirebilirsiniz.</span>
              </div>
            )}
            {/* Role göre çoklu seçim — her dropdown içinde ara + Tümünü seç + checkbox (işaretli = görev dahil) */}
            {subordinates.length === 0 ? (
              <p className="text-xs text-gray-400 mt-2">Atanabilecek personel bulunamadı.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 mt-1">
                {pickerRoleGroups.map(g => (
                  <PeopleCheckDropdown key={g.role} roleKey={g.role} label={g.label} people={g.people}
                    isSelected={isSelected} onToggle={togglePerson} onToggleAll={toggleAll} unavail={unavail} ownerIds={parkOwnerIds} />
                ))}
              </div>
            )}

            {/* Seçilenler özeti */}
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-600">{selectedPeople.length > 0 ? `${selectedPeople.length} görevli seçili` : "Henüz görevli seçilmedi."}</span>
              {selectedPeople.length > 0 && (
                <button type="button" onClick={clearPeople} className="text-[11px] text-red-600 hover:underline">Hepsini kaldır</button>
              )}
            </div>
            {selectedPeople.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedPeople.map(p => (
                  <span key={p.id} className="inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5 text-xs">
                    {p.name}
                    <button type="button" onClick={() => removePerson(p.id)} className="hover:text-red-600"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
            {/* Yürütücü/Bilgi özeti — sürpriz olmasın: kim yapacak, kim bilgi alacak */}
            {features?.executor_model !== false && selectedPeople.length > 0 && (() => {
              // Eşik SABİT: formen ve altı yürütücü; BM/MY bilgi.
              const withRank = selectedPeople.map(p => ({ ...p, rank: rankOfRole(roleOf(subById.get(p.id))) }));
              const execs = withRank.filter(x => x.rank <= ROLE_RANK.formen).map(x => x.name);
              const infos = withRank.filter(x => x.rank > ROLE_RANK.formen).map(x => x.name);
              return (
                <div className="mt-2 text-[11px] rounded-md bg-gray-50 border px-2 py-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  {execs.length > 0 && <span><span className="text-gray-500">🛠️ Yürütücü:</span> <b className="text-emerald-700">{execs.join(", ")}</b></span>}
                  {infos.length > 0 && <span><span className="text-gray-500">👁️ Bilgi (yönetici):</span> {infos.join(", ")}</span>}
                </div>
              );
            })()}
            {(() => {
              // Görev sorumlusu = FORMEN (birden çok seçilebilir). 0 formen → yok (BM dağıtır).
              // 1 formen → otomatik. 2+ → checkbox ile bir VEYA daha fazlasını seç.
              const formenSel = selectedPeople.filter(p => roleOf(subById.get(p.id)) === "formen");
              if (formenSel.length === 0) return null;
              if (formenSel.length === 1) {
                const sp = subById.get(formenSel[0].id);
                return (
                  <div className="mt-2 text-[11px] rounded-md bg-purple-50 border border-purple-200 px-2 py-1.5 text-purple-800">
                    🛠️ Görev sorumlusu (otomatik): <b>{sp?.name || formenSel[0].name}</b>
                  </div>
                );
              }
              const missing = gorevSorumluIds.size === 0;
              return (
                <div className="mt-2">
                  <Label>Görev Sorumlusu <span className="text-red-600">(zorunlu — formen; birden çok seçebilirsin)</span></Label>
                  <div className={`rounded-md border p-2 space-y-1 ${missing ? "border-red-400 bg-red-50" : "border-gray-200"}`}>
                    {[...formenSel].sort((a, b) => (parkOwnerIds.has(a.id) ? 0 : 1) - (parkOwnerIds.has(b.id) ? 0 : 1)).map(p => {
                      const sp = subById.get(p.id);
                      const star = parkOwnerIds.has(p.id) ? "⭐ " : "";
                      const checked = gorevSorumluIds.has(p.id);
                      return (
                        <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" checked={checked}
                                 onChange={() => setGorevSorumluIds(prev => { const n = new Set(prev); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; })} />
                          <span>{star}{sp?.name || p.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  {missing
                    ? <div className="text-[11px] text-red-600 mt-1">En az bir görev sorumlusu seç (istersen birden çok).</div>
                    : <div className="text-[11px] text-gray-500 mt-1">{gorevSorumluIds.size} görev sorumlusu — her biri kendi ekibinden sorumlu, tamamlanma için hepsinin bitirmesi gerekir.</div>}
                </div>
              );
            })()}
          </div>
        </div>
    </DraggableModal>
  );
}

function Chk({ label, v, on }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!v} onChange={on} />
      <span>{label}</span>
    </label>
  );
}

// Serbest harita işareti için: haritaya tıkla → koordinat al.
// single=true → tek tık (alan çizimi, pasif alandaki gibi); aksi halde çift tık
// (tekil nokta seçimi kazara işaret koymasın).
function ClickToMark({ onPick, single = false }) {
  useMapEvents({
    click(e) { if (single) onPick(e.latlng.lat, e.latlng.lng); },
    dblclick(e) { if (!single) onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

// Seçilen konum değişince haritayı oraya getir
function RecenterMap({ center, zoom }) {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!center || center[0] == null || center[1] == null) return;
    const key = `${center[0]},${center[1]},${zoom || ""}`;
    if (lastKey.current === key) return; // aynı konum → tekrar ortalama (her render'da snap-back olmasın)
    lastKey.current = key;
    map.setView(center, zoom || Math.max(map.getZoom(), 15));
  }, [center, zoom, map]);
  return null;
}

// Tüm noktaları kadraja sığdır — ama yalnızca noktalar GERÇEKTEN değişince.
// (Kart canlı güncellemede order yeni nesne olur, points yeni referans alır;
//  imza kontrolü olmadan her tazelemede kadraj resetlenir ve kullanıcının
//  yaptığı zoom/pan ~2-3 sn sonra geri alınırdı.)
function FitBounds({ points }) {
  const map = useMap();
  const lastSig = useRef(null);
  useEffect(() => {
    if (!points || points.length === 0) return;
    const sig = JSON.stringify(points);
    if (lastSig.current === sig) return; // aynı noktalar → tekrar oturma (snap-back yok)
    lastSig.current = sig;
    if (points.length === 1) { map.setView(points[0], 15); return; }
    try { map.fitBounds(points, { padding: [25, 25], maxZoom: 16 }); } catch { /* noop */ }
  }, [map, points]);
  return null;
}

// Konum türüne göre sınır poligonu rengi (park=yeşil, pasif=amber, protokol=indigo)
const LOC_POLY_COLORS = {
  park: { color: "#16a34a", fill: "#16a34a" },
  pasif_alan: { color: "#d97706", fill: "#fbbf24" },
  protokol: { color: "#4f46e5", fill: "#818cf8" },
  serbest: { color: "#7c3aed", fill: "#a78bfa" },
};

// Emir detayında konum mini-haritası — marker'a tıkla yol tarifi, "Büyüt" ile büyük harita.
// Kayıtlı park/pasif alan/protokol sınırları çizilmişse poligon olarak gösterilir;
// Harita/Uydu geçişi hem kartta hem büyük görünümde mevcut.
function LocationMiniMap({ order, big = false, onExpand }) {
  const [layer, setLayer] = useState("street");
  const locs = (order.locations && order.locations.length > 0)
    ? order.locations
    : (order.location_label ? [{ label: order.location_label, lat: order.location_lat, lng: order.location_lng }] : []);
  const items = locs.map(l => ({
    c: locCoords(l),
    label: l.label || l.kind || "konum",
    note: l.note || null,
    // İşaretli nokta (alan içinde tek tek koyulan ör. kurumuş ağaç): pin'i var
    isPin: (l.pin_lat != null && l.pin_lng != null) || Array.isArray(l.pin),
    kind: l.kind,
    boundaries: Array.isArray(l.boundaries) && l.boundaries.length > 2 ? l.boundaries : null,
  })).filter(x => x.c || x.boundaries);
  if (items.length === 0) return null;
  // Kadraj: hem marker noktaları hem sınır köşeleri sığsın
  const fitPoints = [];
  items.forEach(x => {
    if (x.c) fitPoints.push(x.c);
    if (x.boundaries) x.boundaries.forEach(p => fitPoints.push(p));
  });
  const center = items.find(x => x.c)?.c || items[0].boundaries[0];
  return (
    <div className="relative rounded-md overflow-hidden border" style={{ height: big ? 460 : 190 }}>
      <MapContainer center={center} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom={big}>
        <TileLayer key={layer} url={TILE_LAYERS[layer].url} attribution={TILE_LAYERS[layer].attribution} />
        <FitBounds points={fitPoints} />
        {items.map((x, i) => (
          x.boundaries ? (
            <Polygon key={`poly-${i}`} positions={x.boundaries}
              pathOptions={{
                color: (LOC_POLY_COLORS[x.kind] || LOC_POLY_COLORS.park).color,
                fillColor: (LOC_POLY_COLORS[x.kind] || LOC_POLY_COLORS.park).fill,
                fillOpacity: 0.25, weight: 2,
              }}>
              <LeafletTooltip sticky>{x.label}{x.note ? ` — ${x.note}` : ""}</LeafletTooltip>
            </Polygon>
          ) : null
        ))}
        {items.map((x, i) => (
          x.c ? (
            x.isPin ? (
              // İşaretli nokta → fuşya daire (öncelik renkleriyle karışmasın) + not
              <CircleMarker key={`pin-${i}`} center={x.c} radius={7}
                pathOptions={{ color: "#86198f", fillColor: "#d946ef", fillOpacity: 0.95, weight: 2 }}
                eventHandlers={{ click: () => window.open(mapsDirUrl(x.c[0], x.c[1]), "_blank", "noopener") }}>
                <LeafletTooltip>{x.label}{x.note ? ` — ${x.note}` : ""} — tıkla: yol tarifi</LeafletTooltip>
              </CircleMarker>
            ) : (
              <Marker key={`mk-${i}`} position={x.c}
                eventHandlers={{ click: () => window.open(mapsDirUrl(x.c[0], x.c[1]), "_blank", "noopener") }}>
                <LeafletTooltip>{x.label}{x.note ? ` — ${x.note}` : ""} — tıkla: yol tarifi</LeafletTooltip>
              </Marker>
            )
          ) : null
        ))}
      </MapContainer>
      <div className="absolute top-1 right-1 z-[1000] flex gap-1">
        {Object.entries(TILE_LAYERS).map(([k, cfg]) => (
          <button key={k} type="button" onClick={() => setLayer(k)}
            className={`text-[11px] rounded border px-1.5 py-0.5 shadow ${layer === k ? "bg-green-600 text-white border-green-600" : "bg-white/90 text-gray-700"}`}>
            {cfg.label}
          </button>
        ))}
        {!big && onExpand && (
          <button type="button" onClick={onExpand}
            className="text-[11px] bg-white/90 border rounded px-2 py-0.5 shadow hover:bg-white">🔍 Büyüt</button>
        )}
      </div>
    </div>
  );
}

// Müdür Operasyon Merkezi — tüm görülebilen emirler haritada renkli marker
function OperationCenterMap({ apiBase, parks, onOpen }) {
  const [points, setPoints] = useState([]);
  const [mapLayer, setMapLayer] = useState("street");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${apiBase}/map`)
      .then(r => { if (alive) setPoints(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apiBase]);

  const center = useMemo(() => {
    if (points.length) return [points[0].lat, points[0].lng];
    const p = (parks || []).find(x => x.latitude != null && x.longitude != null);
    return p ? [Number(p.latitude), Number(p.longitude)] : [39.925, 32.866];
  }, [points, parks]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex gap-2 text-xs flex-wrap">
          {MAP_LEGEND.map(x => (
            <span key={x.l} className="inline-flex items-center gap-1">
              <span className="h-3 w-3 rounded-full inline-block" style={{ background: x.c }} />{x.l}
            </span>
          ))}
        </div>
        <div className="flex gap-1">
          {Object.entries(TILE_LAYERS).map(([key, cfg]) => (
            <button key={key} type="button" onClick={() => setMapLayer(key)}
              className={`text-xs rounded-md border px-2 py-1 ${mapLayer === key ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-600 hover:bg-gray-50"}`}>{cfg.label}</button>
          ))}
        </div>
      </div>
      <div className="h-[70vh] rounded-md overflow-hidden border">
        <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }}>
          <TileLayer key={mapLayer} url={TILE_LAYERS[mapLayer].url} attribution={TILE_LAYERS[mapLayer].attribution} />
          <RecenterMap center={center} zoom={12} />
          {/* Görev yeri sınırları (kayıtlı park/pasif/protokol/serbest çizili alan) —
              öncelik renginde poligon; üstüne gelince "Öncelik · Yer", tıkla → detay. */}
          {points.flatMap(o => (
            Array.isArray(o.boundaries) ? o.boundaries
              .filter(poly => Array.isArray(poly) && poly.length > 2)
              .map((poly, i) => (
                <Polygon key={`ob-${o.id}-${i}`} positions={poly}
                  pathOptions={{ color: markerColor(o), fillColor: markerColor(o), fillOpacity: 0.2, weight: 2 }}
                  eventHandlers={{ click: () => onOpen(o.id) }}>
                  <LeafletTooltip sticky>{priorityInfo(o.priority).label}{o.location_label ? ` · ${o.location_label}` : ` · ${o.title}`}</LeafletTooltip>
                </Polygon>
              )) : []
          ))}
          {points.map(o => (
            <CircleMarker key={o.id} center={[o.lat, o.lng]} radius={9}
              pathOptions={{ color: markerColor(o), fillColor: markerColor(o), fillOpacity: 0.85, weight: 1 }}
              eventHandlers={{ click: () => onOpen(o.id) }}>
              <LeafletTooltip>{priorityInfo(o.priority).label}{o.location_label ? ` · ${o.location_label}` : ` · ${o.title}`}</LeafletTooltip>
            </CircleMarker>
          ))}
          {/* Alan içinde işaretlenen noktalar (ör. kurumuş ağaç) — kırmızı daire + not */}
          {points.flatMap(o => (
            Array.isArray(o.pins) ? o.pins
              .filter(p => p && p.lat != null && p.lng != null)
              .map((p, i) => (
                <CircleMarker key={`pin-${o.id}-${i}`} center={[p.lat, p.lng]} radius={5}
                  pathOptions={{ color: "#86198f", fillColor: "#d946ef", fillOpacity: 1, weight: 2 }}
                  eventHandlers={{ click: () => onOpen(o.id) }}>
                  <LeafletTooltip>{p.note ? p.note : (p.label || "işaretli nokta")}{p.note && p.label ? ` · ${p.label}` : ""}</LeafletTooltip>
                </CircleMarker>
              )) : []
          ))}
        </MapContainer>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">
        {loading ? "Yükleniyor..." : `${points.length} konumlu emir. Markera tıkla → detay.`}
      </p>
    </div>
  );
}

// Müdür için KPI / operasyon raporu
function OperationReport({ apiBase }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true; setLoading(true);
    axios.get(`${apiBase}/report`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apiBase]);

  if (loading || !data) {
    return <Card><CardContent className="py-10 text-center text-gray-400">{loading ? "Yükleniyor..." : "Veri yok"}</CardContent></Card>;
  }

  const Kpi = ({ label, value, sub, cls }) => (
    <Card><CardContent className="p-4">
      <div className={`text-2xl font-bold ${cls || "text-gray-800"}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </CardContent></Card>
  );

  const Bars = ({ title, obj, labelFn }) => {
    const entries = Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);
    const max = entries.reduce((m, [, v]) => Math.max(m, v), 0) || 1;
    return (
      <Card><CardContent className="p-4">
        <div className="font-semibold text-gray-700 mb-2">{title}</div>
        <div className="space-y-1.5">
          {entries.length === 0 && <div className="text-xs text-gray-400">—</div>}
          {entries.map(([k, v]) => (
            <div key={k} className="text-xs">
              <div className="flex justify-between">
                <span>{labelFn(k)}</span>
                <span className="text-gray-500">{v} ({data.total ? Math.round(100 * v / data.total) : 0}%)</span>
              </div>
              <div className="h-2 bg-gray-100 rounded"><div className="h-2 bg-green-500 rounded" style={{ width: `${100 * v / max}%` }} /></div>
            </div>
          ))}
        </div>
      </CardContent></Card>
    );
  };

  const sourceLabel = (k) => (SOURCES.find(s => s.value === k) || {}).label || (k === "belirtilmemis" ? "Belirtilmemiş" : k);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Toplam Emir" value={data.total} />
        <Kpi label="Tamamlanma Oranı" value={`%${data.completion_rate}`} sub={`${data.completed} tamamlandı · ${data.active} açık`} cls="text-green-600" />
        <Kpi label="Geciken" value={data.overdue} cls="text-red-600" />
        <Kpi label="Ort. Tamamlama" value={data.avg_completion_hours != null ? `${data.avg_completion_hours} sa` : "—"} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Bars title="Duruma Göre" obj={data.by_status} labelFn={k => statusInfo(k).label} />
        <Bars title="Önceliğe Göre" obj={data.by_priority} labelFn={k => priorityInfo(k).label} />
        <Bars title="Kaynağa Göre" obj={data.by_source} labelFn={sourceLabel} />
        <Bars title="Kategoriye Göre" obj={data.by_category} labelFn={k => catLabel(k)} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detay + aksiyon dialog'u
// ---------------------------------------------------------------------------

function OrderDetailDialog({ API, apiBase, orderId, personnelId, authz, subordinates, personnel = [], features = { executor_model: true }, parks = [], passiveAreas = [], protokolParks = [], onClose, onChanged, onEdit }) {
  const [order, setOrder] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null); // referans görsel büyük önizleme (null=kapalı)
  const [blockOpen, setBlockOpen] = useState(false); // İş Engeli Bildir dialog
  const [blockNote, setBlockNote] = useState("");
  // İsim → rol/formen çözümü (talimatlar + görevliler için)
  const personById = useMemo(() => {
    const m = new Map();
    (personnel || []).forEach(p => { if (p && p.id) m.set(p.id, p); });
    (subordinates || []).forEach(p => { if (p && p.id && !m.has(p.id)) m.set(p.id, p); });
    return m;
  }, [personnel, subordinates]);
  const roleOfId = (id) => { const p = id && personById.get(id); return p ? roleLabel(roleOf(p)) : null; };
  // Rol: önce backend slug'ı (kesin), yoksa personel listesinden (yedek)
  const roleName = (slug, id) => (slug ? roleLabel(slug) : roleOfId(id));
  const { getTeamById: getTeamByIdDetail } = useTeams();
  const teamLabelOf = (id) => {
    const p = id && personById.get(id);
    const t = p && p.primary_team_id ? getTeamByIdDetail(p.primary_team_id) : null;
    return t && !t.is_default ? (t.display_name || t.slug) : null;
  };
  // Görevlileri FORMEN bazında grupla — assignee verisinden (backend: personnel_role/formen_id),
  // frontend personel listesine bağımlı değil; kalabalıkta okunur kalır.
  const groupKeyOf = (a) => {
    if (a.assignee_kind === "team") return `team:${a.team_id}`;
    if (a.personnel_role === "formen") return a.personnel_id;
    if (a.formen_id) return a.formen_id;
    return a.personnel_id || a.id;
  };
  const assigneeGroups = useMemo(() => {
    const groups = new Map();
    (order?.assignees || []).forEach(a => {
      const key = groupKeyOf(a);
      if (!groups.has(key)) groups.set(key, { key, members: [] });
      groups.get(key).members.push(a);
    });
    return [...groups.values()].map(g => {
      const selfM = g.members.find(m => m.personnel_id === g.key); // grup lideri (formen) — görevliyse
      const teamM = String(g.key).startsWith("team:") ? g.members[0] : null;
      const lead = personById.get(g.key);
      const leaderName = selfM?.personnel_name || lead?.name || teamM?.team_name || "Görevliler";
      const leaderRole = selfM?.personnel_role ? roleLabel(selfM.personnel_role)
        : (lead ? roleLabel(roleOf(lead)) : (teamM ? "Ekip" : "Formen"));
      const done = g.members.filter(m => ["tamamlandi", "onaylandi"].includes(m.assignee_status)).length;
      const started = g.members.filter(m => ["basladi", "devam"].includes(m.assignee_status)).length;
      return { ...g, leaderName, leaderRole, done, started, total: g.members.length };
    });
  }, [order?.assignees, personById]); // eslint-disable-line react-hooks/exhaustive-deps
  const [openAG, setOpenAG] = useState(null);
  const agDefaultOpen = assigneeGroups.length <= 2;
  const agOpen = (key) => (openAG === null ? agDefaultOpen : openAG.has(key));
  const toggleAG = (key) => setOpenAG(prev => {
    const cur = prev === null ? new Set(agDefaultOpen ? assigneeGroups.map(g => g.key) : []) : new Set(prev);
    cur.has(key) ? cur.delete(key) : cur.add(key);
    return cur;
  });
  const [expandedEv, setExpandedEv] = useState(() => new Set());
  const toggleEv = (id) => setExpandedEv(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [busy, setBusy] = useState(false);
  const [actionPhase, setActionPhase] = useState(null); // 'start' | 'complete'
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [mapBig, setMapBig] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false); // geçmiş: tüm hareketler mi, sadece önemli mi
  const [showHistory, setShowHistory] = useState(false); // Görev Geçmişi bölümü açık mı (varsayılan kapalı)

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${apiBase}/${orderId}`);
      setOrder(r.data);
    } catch (e) {
      toast.error("Detay yüklenemedi");
      onClose();
    }
  }, [apiBase, orderId, onClose]);

  useEffect(() => { load(); }, [load]);

  // Detay açıkken canlı güncelleme: bu emir için güncelleme gelirse tazele, silinirse kapat.
  // (Üst rol geri çekerse/iptal ederse görevlinin açık kartı anında güncellenir.)
  useEffect(() => {
    const onUpd = (e) => { if (!e?.detail?.order_id || e.detail.order_id === orderId) load(); };
    const onDel = (e) => { if (e?.detail?.order_id === orderId) onClose(); };
    window.addEventListener("operation_updated", onUpd);
    window.addEventListener("operation_assigned", onUpd);
    window.addEventListener("operation_deleted", onDel);
    return () => {
      window.removeEventListener("operation_updated", onUpd);
      window.removeEventListener("operation_assigned", onUpd);
      window.removeEventListener("operation_deleted", onDel);
    };
  }, [orderId, load, onClose]);

  // Açılınca otomatik "görüldü"
  useEffect(() => {
    if (!order) return;
    const me = (order.assignees || []).find(a => a.personnel_id === personnelId);
    if (me && me.assignee_status === "iletildi") {
      axios.post(`${apiBase}/${orderId}/seen`).then(() => { load(); onChanged?.(); }).catch(() => {});
    }
  }, [order, apiBase, orderId, personnelId, load, onChanged]);

  const act = async (action, body) => {
    setBusy(true);
    try {
      await axios.post(`${apiBase}/${orderId}/${action}`, body || {});
      toast.success("Güncellendi");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  if (!order) return null;
  const si = statusInfo(order.status);
  const pi = priorityInfo(order.priority);
  const me = (order.assignees || []).find(a => a.personnel_id === personnelId);
  const isClosed = ["iptal", "geri_cekildi", "onaylandi"].includes(order.status);
  // can_manage = emri veren VEYA emri verenin ÜST zinciri (iptal/sil/devret yetkisi).
  // Aradaki roller (görevlinin üstü ama oluşturanın altı) → can_manage=false → yalnız görür.
  const canManage = !!order.can_manage;
  // YÜRÜTÜCÜ/BİLGİ modeli — eşik SABİT: formen ve altı (PG/çim) = yürütücü (işi yapar);
  // BM / MY / müdür HER ZAMAN bilgi/yönetici (Başla/Tamamla görmez, izler + müdahale eder).
  const execModel = features?.executor_model !== false;
  const workerThreshold = ROLE_RANK.formen;
  const meRank = me ? rankOfRole(me.personnel_role) : 99;
  const isWorker = !!me && meRank <= workerThreshold;   // yürütücü (formen ve altı)
  const isBilgi = !!me && meRank > workerThreshold;      // BM/MY → sadece bilgi/izleme
  // Görev sorumlusu = kanıt sorumlusu (must_provide) işaretli FORMEN(ler) VEYA seçilen görev
  // sorumlusu (leader_personnel_id) — kanıt istenmese de görev sorumlusu seçilebilir.
  // Ekip sorumlusu = must_provide işaretli SAHA (PG/çim, formen altı) kişi(ler).
  const gorevSorumlulari = (order.assignees || []).filter(a =>
    a.assignee_kind !== "team" && a.personnel_role &&
    rankOfRole(a.personnel_role) === ROLE_RANK.formen &&
    ((a.must_provide_start_evidence || a.must_provide_end_evidence) ||
     (order.leader_personnel_id && a.personnel_id === order.leader_personnel_id)));
  const gorevSorumluPids = new Set(gorevSorumlulari.map(a => a.personnel_id));
  // Emrin konumu kimin bölgesindeyse o kişiler (⭐ ile işaretlenir — alt görevlendirmede kolay seçim)
  const ownerIds = (() => {
    const byId = new Map((subordinates || []).map(p => [p.id, p]));
    const s = new Set();
    const chainOf = (pid) => {
      if (!pid) return; s.add(pid); const p = byId.get(pid);
      if (p) { if (p.formen_id) s.add(p.formen_id); if (p.bolge_muhendisi_id) s.add(p.bolge_muhendisi_id); if (p.mudur_yardimcisi_id) s.add(p.mudur_yardimcisi_id); }
    };
    const itemOwners = (item) => {
      if (!item) return;
      (item.assigned_personnel_ids || []).forEach(chainOf); chainOf(item.assigned_formen_id);
      const bm = item.bolge_muhendisi_id || item.assigned_bolge_muhendisi_id;
      const my = item.mudur_yardimcisi_id || item.assigned_mudur_yardimcisi_id;
      if (bm) s.add(bm); if (my) s.add(my);
    };
    const allItems = [...(parks || []), ...(passiveAreas || []), ...(protokolParks || [])];
    (order.locations || []).forEach(l => {
      if (l.ref_id) { itemOwners(allItems.find(x => x.id === l.ref_id)); return; }
      const c = (l.pin_lat != null && l.pin_lng != null) ? [Number(l.pin_lat), Number(l.pin_lng)]
        : (l.lat != null && l.lng != null ? [Number(l.lat), Number(l.lng)] : null);
      if (!c) return;
      let hit = allItems.find(x => Array.isArray(x.boundaries) && x.boundaries.length >= 3 && pointInPolygon(c, x.boundaries));
      if (!hit) { let bestD = Infinity; for (const x of allItems) { const lat = x.latitude != null ? Number(x.latitude) : null, lng = x.longitude != null ? Number(x.longitude) : null; if (lat == null || lng == null) continue; const d = (lat - c[0]) ** 2 + (lng - c[1]) ** 2; if (d < bestD) { bestD = d; hit = x; } } }
      itemOwners(hit);
    });
    return s;
  })();
  // Başla/Tamamla yalnızca yürütücü katmanında (model açıkken). Bilgi'de gizli.
  const canComplete = authz.has?.("operasyon.tamamla") && me && !isClosed && (!execModel || isWorker);
  // İş Engeli Bildir: yalnızca yürütücü işin tıkandığını üst amirlere bildirir.
  const canReportBlock = authz.has?.("operasyon.is_engeli_bildir") && me && !isClosed &&
    order.status !== "tamamlandi" && (!execModel || isWorker);
  const canApprove = authz.has?.("operasyon.onayla") && order.requires_approval && order.status === "tamamlandi";
  const canCancel = authz.has?.("operasyon.olustur") && canManage && !isClosed;
  const canPlan = order.order_type === "planlamaya_dahil" && !isClosed && authz.has?.("operasyon.tamamla") &&
    (me || canManage);
  const canDelete = authz.has?.("operasyon.sil") && canManage;
  // Tepe roller (müdür/admin/başkan): emri kendileri oluşturur, üstlerinden görev
  // almazlar → "Alt Görevlendir" onların panelinde mantıksız, gizlenir.
  const isTopRole = ["mudur", "admin", "baskan"].includes(authz.userRole || authz.role);
  // Görevlilerden EN AZ BİRİNİ yöneten (üst zincir) kişi — atanan olmasa da, emri
  // verenin altında olsa da — personel ekleyip çıkarabilmeli (MY gibi ara yöneticiler).
  const subIds = new Set((subordinates || []).map(s => s.id));
  const managesAnAssignee = (order.assignees || []).some(a => a.personnel_id && subIds.has(a.personnel_id));
  const canDelegate = authz.has?.("operasyon.devret") && !isClosed &&
    order.status !== "tamamlandi" && (me || canManage || managesAnAssignee) && !isTopRole;
  // Atanan OLMAYAN ama emri yöneten kişi (oluşturan üstü / görevliyi yöneten ara amir) →
  // bilgi/yönetici olarak görür: izler + (yetkisi varsa) personel ekle-çıkar / iptal-geri çek.
  const isManagerViewer = !me && (canManage || managesAnAssignee);

  const doDelete = async () => {
    setBusy(true);
    try {
      await axios.delete(`${apiBase}/${orderId}`);
      toast.success("Görev silindi");
      setConfirmDel(false);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Silinemedi");
    } finally { setBusy(false); }
  };

  // Yanlış görevlendirileni çıkar (başlamamış + ekleyen/üst yetkisi)
  const canRemoveAssignee = (a) =>
    !isClosed && (order.assignees || []).length > 1 &&
    ["iletildi", "goruldu"].includes(a.assignee_status) &&
    a.personnel_id !== personnelId &&  // kimse kendini görevden çıkaramaz
    (canManage || (a.personnel_id && subIds.has(a.personnel_id)));
  const removeAssignee = async (a) => {
    setBusy(true);
    try {
      await axios.delete(`${apiBase}/${orderId}/assignees/${a.id}`);
      toast.success(`${a.personnel_name || "Görevli"} çıkarıldı`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Çıkarılamadı");
    } finally { setBusy(false); }
  };

  return (
    <>
    <DraggableModal
      isOpen
      onClose={onClose}
      maxWidth="max-w-2xl"
      title="Anlık Görevlendirme Detayı"
      subtitle={`${order.title} · Oluşturan: ${order.created_by_name || "—"}`}
      footer={
        <div className="flex flex-wrap gap-2 justify-end">
          {order.status === "geri_cekildi" && canManage && onEdit && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => onEdit(order)} disabled={busy}>
              <Edit2 className="h-4 w-4 mr-1" /> Düzenle ve Yeniden Gönder
            </Button>
          )}
          {canPlan && (
            <Button size="sm" variant="outline" className="text-blue-700 border-blue-300" onClick={() => setPlanOpen(true)} disabled={busy}>
              <Clock className="h-4 w-4 mr-1" /> {order.planned_at ? "Planı Değiştir" : "Planla"}
            </Button>
          )}
          {canComplete && me.assignee_status !== "tamamlandi" && me.assignee_status !== "basladi" && me.assignee_status !== "devam" && (
            <Button size="sm" variant="outline" onClick={() => setActionPhase("start")} disabled={busy}>
              <Play className="h-4 w-4 mr-1" /> Başla
            </Button>
          )}
          {canComplete && me.assignee_status !== "tamamlandi" && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setActionPhase("complete")} disabled={busy}>
              <Check className="h-4 w-4 mr-1" /> Tamamla
            </Button>
          )}
          {canReportBlock && me.assignee_status !== "tamamlandi" && (
            <Button size="sm" variant="outline" className="text-orange-700 border-orange-300" onClick={() => setBlockOpen(true)} disabled={busy} title="İşin tıkandığını/engeli üst amirlere bildir">
              <AlertTriangle className="h-4 w-4 mr-1" /> İş Engeli Bildir
            </Button>
          )}
          {canDelegate && (
            <Button size="sm" variant="outline" onClick={() => setDelegateOpen(true)} disabled={busy}
                    title={isWorker ? "İşi alt hiyerarşine dağıt" : "Göreve personel ekle (yönetici müdahalesi)"}>
              <Users className="h-4 w-4 mr-1" /> {isWorker ? "Alt Görevlendir" : "Personel Ekle"}
            </Button>
          )}
          {canApprove && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act("approve")} disabled={busy}>
              <ShieldCheck className="h-4 w-4 mr-1" /> Onayla
            </Button>
          )}
          {canCancel && (
            <>
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={() => act("withdraw", { reason: "Geri çekildi" })} disabled={busy}>
                <Undo2 className="h-4 w-4 mr-1" /> Geri Çek
              </Button>
              <Button size="sm" variant="outline" className="text-red-700 border-red-300" onClick={() => act("cancel", { reason: "İptal edildi" })} disabled={busy}>
                <Ban className="h-4 w-4 mr-1" /> İptal Et
              </Button>
            </>
          )}
          {canDelete && (
            <Button size="sm" variant="outline" className="text-red-700 border-red-400 bg-red-50" onClick={() => setConfirmDel(true)} disabled={busy}>
              <Trash2 className="h-4 w-4 mr-1" /> Sil
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose}>Kapat</Button>
        </div>
      }
    >
        <div className="space-y-3 text-sm">
          {/* Durum / öncelik rozetleri */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={si.cls}>{si.label}</Badge>
            <Badge variant="outline" className={pi.cls}>{pi.label}</Badge>
            {order.is_collective && <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">Toplu Çalışma</Badge>}
            {order.is_overdue && <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">Gecikti</Badge>}
            {order.order_type === "planlamaya_dahil" && !order.planned_at &&
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">Planlanacak</Badge>}
          </div>

          {/* Görev sorumlusu (formen+kanıt) / yürütücü / gönderilen isimleri */}
          {execModel && (() => {
            const isCreator = order.created_by_id === personnelId;
            const persons = (order.assignees || []).filter(a => a.assignee_kind !== "team" && a.personnel_role);
            const sorumluNames = gorevSorumlulari.map(a => a.personnel_name).filter(Boolean);
            const yurutucuNames = persons.filter(a => rankOfRole(a.personnel_role) <= workerThreshold).map(a => a.personnel_name).filter(Boolean);
            const maxR = persons.length ? Math.max(...persons.map(a => rankOfRole(a.personnel_role))) : null;
            const sentTo = persons.filter(a => rankOfRole(a.personnel_role) === maxR).map(a => a.personnel_name).filter(Boolean);
            const sorumluBit = sorumluNames.length
              ? <>Görev sorumlusu: <b>{sorumluNames.join(", ")}</b>.</>
              : (yurutucuNames.length ? <>Yürütücü: <b>{yurutucuNames.join(", ")}</b>.</> : <>Henüz görev sorumlusu atanmadı.</>);
            // Türkçe yönelme eki (X'a / X'e / X'ya / X'ye)
            const dative = (name) => {
              const s = String(name || "").trim();
              if (!s) return s;
              const vs = s.toLocaleLowerCase("tr").match(/[aeıioöuü]/g) || [];
              const last = vs.length ? vs[vs.length - 1] : "a";
              const back = "aıou".includes(last);
              const buf = /[aeıioöuü]/i.test(s.slice(-1)) ? "y" : "";
              return `${s}'${buf}${back ? "a" : "e"}`;
            };
            if (isCreator) {
              return (
                <div className="flex items-start gap-2 rounded-md bg-indigo-50 border border-indigo-200 px-2.5 py-1.5 text-xs text-indigo-800">
                  <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Bu görevi <b>siz oluşturdunuz</b>{sentTo.length === 1
                      ? <>, <b>{dative(sentTo[0])}</b> gönderdiniz</>
                      : (sentTo.length > 1 ? <>, şu kişilere gönderdiniz: <b>{sentTo.join(", ")}</b></> : null)}. {sorumluBit} Üst yöneticiler <b>bilgi</b> görür; Başla/Tamamla görevi yürütücünündür.
                  </span>
                </div>
              );
            }
            if (isBilgi || isManagerViewer) {
              return (
                <div className="flex items-start gap-2 rounded-md bg-sky-50 border border-sky-200 px-2.5 py-1.5 text-xs text-sky-800">
                  <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Bu görevi <b>{me ? "bilgi" : "yönetici"}</b> olarak görüyorsun. {sorumluBit} Başla/Tamamla yürütücüye aittir.{canDelegate ? " Personel ekleyip çıkarabilirsin." : ""}{canManage ? " Görevi iptal/geri çekebilirsin." : ""}
                  </span>
                </div>
              );
            }
            return null;
          })()}

          {/* Ekip sorumlusu banner'ı: bu görevde kanıt yüklemekle sorumlu olan kişi kendi panelinde görsün */}
          {me && (me.must_provide_start_evidence || me.must_provide_end_evidence) && rankOfRole(me.personnel_role) < ROLE_RANK.formen && (() => {
            const types = (px) => [
              order[`need_${px}_photo`] && "Fotoğraf",
              order[`need_${px}_video`] && "Video",
              order[`need_${px}_gps`] && "Konum",
            ].filter(Boolean).join(", ");
            const start = me.must_provide_start_evidence ? types("start") : "";
            const end = me.must_provide_end_evidence ? types("end") : "";
            return (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-xs text-amber-800">
                <span className="mt-0.5">📸</span>
                <span>
                  Bu görevde <b>ekip sorumlususun</b> (sadece bu görev için) — istenen kanıtı yüklemen gerekiyor:
                  {start && <> <b>Başlangıç:</b> {start}</>}
                  {start && end && " · "}
                  {end && <> <b>Bitiş:</b> {end}</>}
                </span>
              </div>
            );
          })()}

          {/* Seçilen alanlar — yöneticiler ne istendiğini görür */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <div><span className="text-gray-400">Kategori:</span> {catLabel(order.task_category)}</div>
            {order.task_type && <div><span className="text-gray-400">Görev Türü:</span> {order.task_type}</div>}
            <div><span className="text-gray-400">Öncelik:</span> {priorityInfo(order.priority).label}</div>
            <div><span className="text-gray-400">Emir Türü:</span> {orderTypeLabel(order.order_type)}</div>
            {order.operation_effect && <div><span className="text-gray-400">Operasyon Etkisi:</span> {effectLabel(order.operation_effect)}</div>}
            {order.source && <div><span className="text-gray-400">Kaynak:</span> {sourceLabel(order.source)}</div>}
            {order.scheduled_date && <div><span className="text-gray-400">Tarih:</span> {fmtTR(order.scheduled_date)}{order.start_time ? ` ${order.start_time}` : ""}{order.end_time ? `–${order.end_time}` : ""}</div>}
            {order.due_date && <div><span className="text-gray-400">Son Tarih:</span> {fmtTR(order.due_date)}</div>}
            {order.completion_result && <div><span className="text-gray-400">Sonuç:</span> {order.completion_result}</div>}
          </div>
          {order.operation_effect === "tam_gun" && <p className="text-[11px] text-gray-400">Tam gün → günlük işleri "ertelendi" sayılır, eksik değil.</p>}

          {/* İstenen kanıtlar — tamamlarken yüklenmesi istenenler (müdür talebi) */}
          {evidenceList(order).length > 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs">
              <span className="text-slate-500 font-medium">İstenen Kanıtlar:</span>{" "}
              {evidenceList(order).map((e, i) => (
                <Badge key={i} variant="outline" className="bg-white text-slate-700 border-slate-300 mr-1 mb-1">{e}</Badge>
              ))}
            </div>
          )}

          {/* Talimatlar — açıklama (oluşturan) + alt görevlendirme notları (delegatör), kim yazdı belli */}
          {(order.description || (order.events || []).some(e => e.event_type === "delegated" && delegInstruction(e.note))) && (
            <div>
              <div className="font-semibold text-gray-700 mb-0.5">Talimatlar</div>
              {order.description && (
                <div className="text-sm text-gray-700">{order.description} <span className="text-gray-400 text-[11px]">— {order.created_by_name || "oluşturan"}{roleName(order.created_by_role, order.created_by_id) ? ` (${roleName(order.created_by_role, order.created_by_id)})` : ""}</span></div>
              )}
              {(order.events || []).filter(e => e.event_type === "delegated" && delegInstruction(e.note)).map(e => (
                <div key={e.id} className="text-sm text-gray-700">{delegInstruction(e.note)} <span className="text-gray-400 text-[11px]">— {e.actor_name || "görevlendiren"}{roleName(e.actor_role, e.actor_id) ? ` (${roleName(e.actor_role, e.actor_id)})` : ""}</span></div>
              ))}
            </div>
          )}

          {/* Konum — tıklanabilir (yol tarifi) */}
          <div className="text-xs">
            <span className="text-gray-400">Konum:</span>{" "}
            {((order.locations && order.locations.length > 0)
              ? order.locations
              : (order.location_label ? [{ label: order.location_label, lat: order.location_lat, lng: order.location_lng }] : [])
            ).map((l, i) => {
              const c = locCoords(l);
              const label = l.label || l.kind || "konum";
              return (
                <span key={i} className="mr-2">
                  {c
                    ? <a href={mapsDirUrl(c[0], c[1])} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5" title="Yol tarifi al ve git">
                        <MapPin className="h-3 w-3" />{label}
                      </a>
                    : <span className="inline-flex items-center gap-0.5 text-gray-600"><MapPin className="h-3 w-3" />{label}</span>}
                  {l.note && <span className="text-amber-700" title="işaret notu"> — {l.note}</span>}
                </span>
              );
            })}
          </div>

          {/* Konum mini-haritası — marker'a tıkla yol tarifi, Büyüt ile büyük harita */}
          <LocationMiniMap order={order} onExpand={() => setMapBig(true)} />

          {/* Operasyon nedeniyle etkilenen planlı işler */}
          {Array.isArray(order.affected_plans) && order.affected_plans.length > 0 && (
            <div>
              <div className="font-semibold text-gray-700 mb-1">Etkilenen Planlı İşler</div>
              <div className="space-y-0.5">
                {order.affected_plans.map(ap => (
                  <div key={ap.id} className="text-xs flex items-center gap-2 flex-wrap">
                    <span>{ap.activity_label}</span>
                    <Badge variant="outline" className={ap.effect === "ertelendi" ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-gray-100 text-gray-600 border-gray-300"}>
                      {ap.effect === "ertelendi" ? "Ertelendi" : "Etkilenmedi"}
                    </Badge>
                    {ap.reported_by_name && <span className="text-gray-400">— bildiren: {ap.reported_by_name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Referans görseller (görünürlük backend'te filtrelenir) */}
          {Array.isArray(order.reference_media) && order.reference_media.length > 0 && (
            <div>
              <div className="font-semibold text-gray-700">Referans Görseller</div>
              <div className="text-[11px] text-gray-400 mb-1">Emri oluşturan ({order.created_by_name || "—"}) tarafından paylaşılan örnek görseller (yapılacak işi anlatır).</div>
              <div className="grid grid-cols-4 gap-2">
                {order.reference_media.map((m, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setLightboxIdx(idx)}
                    title="Büyük görüntüle"
                    className="block focus:outline-none"
                  >
                    <img src={mediaUrl(API, m.url)} alt={m.name || "referans"} className="h-20 w-full object-cover rounded border hover:opacity-90 cursor-zoom-in" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Görevliler — formen bazında gruplu (kalabalıkta okunur) */}
          <div>
            <div className="font-semibold text-gray-700 mb-1 flex items-center gap-1"><Users className="h-4 w-4" /> Görevliler ({(order.assignees || []).length})</div>
            <div className="space-y-1">
              {assigneeGroups.map(g => {
                const open = agOpen(g.key);
                return (
                  <div key={g.key} className="border rounded-md overflow-hidden">
                    <button type="button" onClick={() => toggleAG(g.key)}
                      className="w-full flex items-center justify-between px-2 py-1.5 bg-gray-50 hover:bg-gray-100 text-left">
                      <span className="text-xs font-medium flex items-center gap-1 flex-wrap">
                        <span className="text-gray-400 w-3 inline-block">{open ? "▾" : "▸"}</span>
                        {g.leaderName}{g.leaderRole ? ` · ${g.leaderRole}` : ""}
                        <span className="text-gray-400 font-normal">· {g.total} kişi</span>
                        {gorevSorumlulari.some(a => String(a.personnel_id) === String(g.key)) &&
                          <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300 text-[10px] px-1 py-0">Görev Sorumlusu</Badge>}
                      </span>
                      <span className="text-[11px] mr-1">
                        {g.done > 0
                          ? <span className="text-green-700">{g.done}/{g.total} tamamlandı</span>
                          : g.started > 0
                            ? <span className="text-indigo-700">{g.started} başladı</span>
                            : <span className="text-gray-400">bekliyor</span>}
                      </span>
                    </button>
                    {open && (
                      <div className="divide-y">
                        {g.members.map(a => {
                          const role = a.personnel_role ? roleLabel(a.personnel_role) : roleOfId(a.personnel_id);
                          const team = a.personnel_team || teamLabelOf(a.personnel_id);
                          const meta = [role, team].filter(Boolean).join(" · ");
                          return (
                            <div key={a.id} className="text-xs px-2 py-1">
                              <div className="flex items-center justify-between">
                                <span>
                                  {a.personnel_name || a.team_name || "—"}
                                  {meta && <span className="text-gray-400"> · {meta}</span>}
                                  {a.assignee_kind === "team" && <span className="text-gray-400"> (ekip)</span>}
                                  {/* Görev Sorumlusu = kanıt sorumlusu FORMEN veya seçilen lider */}
                                  {a.personnel_id && gorevSorumluPids.has(a.personnel_id) &&
                                    <Badge variant="outline" className="ml-1 bg-purple-100 text-purple-700 border-purple-300" title="Bu görevin sorumlusu — görev sorumlusu">Görev Sorumlusu</Badge>}
                                  {/* Ekip Sorumlusu yalnızca SAHA kişisi (park görevlisi/çim ekibi) için;
                                      formen/üst roller ekip yöneticisidir, "ekip sorumlusu" değildir. */}
                                  {(a.must_provide_start_evidence || a.must_provide_end_evidence) && rankOfRole(a.personnel_role) < ROLE_RANK.formen && (
                                    <>
                                      <Badge variant="outline" className="ml-1 bg-amber-100 text-amber-700 border-amber-300" title="İstenen kanıttan (foto/video/konum) sorumlu">Ekip Sorumlusu</Badge>
                                      <span className="text-gray-300 text-[10px] ml-1">(sadece bu görev için)</span>
                                    </>
                                  )}
                                </span>
                                <div className="flex items-center gap-1">
                                  <Badge variant="outline" className={statusInfo(a.assignee_status).cls}>{statusInfo(a.assignee_status).label}</Badge>
                                  {canRemoveAssignee(a) && (
                                    <button type="button" onClick={() => removeAssignee(a)} disabled={busy}
                                      title="Görevliyi çıkar (yanlış görevlendirme)" className="text-gray-400 hover:text-red-600">
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {(a.start_evidence?.length > 0 || a.start_geofence_ok === false) &&
                                <EvidenceRow API={API} label="Başlangıç" items={a.start_evidence} geo={a.start_geofence_ok} />}
                              {(a.end_evidence?.length > 0 || a.end_geofence_ok === false) &&
                                <EvidenceRow API={API} label="Bitiş" items={a.end_evidence} geo={a.end_geofence_ok} />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Zincir / geçmiş — iki kademe: önemli olaylar (varsayılan) + tüm hareketler */}
          {(() => {
            const allEv = order.events || [];
            const routineCount = allEv.filter(e => !isMilestoneEvent(e.event_type)).length;
            const shown = showAllEvents ? allEv : allEv.filter(e => isMilestoneEvent(e.event_type));
            return (
          <div>
            <button type="button" onClick={() => setShowHistory(v => !v)}
              className="w-full font-semibold text-gray-700 mb-1 flex items-center gap-1 text-left">
              <span className="text-gray-400 w-3 inline-block">{showHistory ? "▾" : "▸"}</span>
              Görev Geçmişi <span className="text-gray-400 font-normal">({allEv.length})</span>
            </button>
            {showHistory && (<>
            {routineCount > 0 && (
              <div className="flex justify-end mb-1">
                <button type="button" onClick={() => setShowAllEvents(v => !v)} className="text-[11px] text-blue-600 hover:underline">
                  {showAllEvents ? "Sadece önemli olaylar" : `Tüm hareketleri göster (${routineCount})`}
                </button>
              </div>
            )}
            <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
              {shown.map(ev => {
                const deleg = ev.event_type === "delegated";
                const names = deleg ? delegNames(ev.note) : [];
                const evOpen = expandedEv.has(ev.id);
                return (
                  <div key={ev.id} className="text-xs text-gray-500 flex gap-2 flex-wrap">
                    <span className="text-gray-400">{fmtTR(ev.occurred_at)}</span>
                    <span>{ev.actor_name || "—"}</span>
                    <span className="font-medium">{eventLabel(ev.event_type)}</span>
                    {ev.to_status && <span>→ {statusInfo(ev.to_status).label}</span>}
                    {ev.note && ev.event_type === "planned" && <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">Planlandı: {fmtTR(ev.note)}</Badge>}
                    {ev.event_type === "planned" && <span className="text-[11px] text-green-700">· haftalık faaliyet planına dahil edildi</span>}
                    {ev.event_type === "removed" && ev.note && <span className="text-red-600">· {ev.note.replace(/^Çıkarıldı:\s*/, "")}</span>}
                    {ev.event_type === "is_engeli" && ev.note && <span className="text-orange-700">· {ev.note}</span>}
                    {deleg && names.length > 0 && (
                      <button type="button" onClick={() => toggleEv(ev.id)} className="text-gray-500 hover:underline">
                        · {names.length} kişi{evOpen ? "" : " (göster)"}
                      </button>
                    )}
                    {deleg && evOpen && names.length > 0 && (
                      <span className="w-full pl-16 text-gray-400">{names.join(", ")}</span>
                    )}
                  </div>
                );
              })}
            </div>
            </>)}
          </div>
            );
          })()}
        </div>
    </DraggableModal>
    {actionPhase && (
      <EvidenceActionDialog
        API={API} apiBase={apiBase} orderId={orderId} order={order} me={me} phase={actionPhase}
        onClose={() => setActionPhase(null)}
        onDone={() => { setActionPhase(null); load(); onChanged?.(); }}
      />
    )}
    {delegateOpen && (
      <DelegateDialog
        apiBase={apiBase} orderId={orderId} subordinates={subordinates} personnelId={personnelId}
        ownerIds={ownerIds}
        existingAssigneeIds={(order.assignees || []).filter(a => a.personnel_id).map(a => a.personnel_id)}
        existingAssignees={(order.assignees || []).filter(a => a.assignee_kind !== "team" && a.personnel_id)}
        scheduledDate={order.scheduled_date}
        needStartEvidence={!!(order.need_start_photo || order.need_start_video || order.need_start_gps)}
        needEndEvidence={!!(order.need_end_photo || order.need_end_video || order.need_end_gps)}
        alreadyStartResponsible={(order.assignees || []).some(a => a.assignee_kind !== "team" && a.must_provide_start_evidence && a.personnel_id !== personnelId)}
        alreadyEndResponsible={(order.assignees || []).some(a => a.assignee_kind !== "team" && a.must_provide_end_evidence && a.personnel_id !== personnelId)}
        actorAlreadyProvides={(order.assignees || []).some(a => a.assignee_kind !== "team" && a.personnel_id === personnelId && (a.must_provide_start_evidence || a.must_provide_end_evidence))}
        evidenceLabels={evidenceList(order)}
        onClose={() => setDelegateOpen(false)}
        onDone={() => { setDelegateOpen(false); load(); onChanged?.(); }}
      />
    )}
    {planOpen && (
      <PlanDialog
        apiBase={apiBase} orderId={orderId} initial={order.scheduled_date}
        onClose={() => setPlanOpen(false)}
        onDone={() => { setPlanOpen(false); load(); onChanged?.(); }}
      />
    )}
    {mapBig && (
      <Dialog open onOpenChange={(o) => { if (!o) setMapBig(false); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {order.title} — Konum</DialogTitle></DialogHeader>
          <LocationMiniMap order={order} big />
          <p className="text-[11px] text-gray-400">İşaretli noktaya tıklayınca yol tarifi açılır.</p>
        </DialogContent>
      </Dialog>
    )}
    {confirmDel && (
      <Dialog open onOpenChange={(o) => { if (!o) setConfirmDel(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Görevi Sil
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Bu görevi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(false)} disabled={busy}>Vazgeç</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={doDelete} disabled={busy}>
              {busy ? "Siliniyor..." : "Evet, Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    {blockOpen && (
      <Dialog open onOpenChange={(o) => { if (!o) { setBlockOpen(false); setBlockNote(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="h-5 w-5" /> İş Engeli Bildir
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500">İşin neden tıkandığını/engeli yaz; üst amirlerine (BM, MY, müdür) bildirim olarak gider.</p>
          <Textarea rows={3} value={blockNote} onChange={e => setBlockNote(e.target.value)}
            placeholder="Örn: 3 kişi izinli, bu personelle yetişmez / malzeme eksik" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBlockOpen(false); setBlockNote(""); }} disabled={busy}>Vazgeç</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" disabled={busy || !blockNote.trim()}
              onClick={async () => {
                setBusy(true);
                try {
                  await axios.post(`${apiBase}/${orderId}/report-block`, { note: blockNote.trim() });
                  toast.success("İş engeli üst amirlere bildirildi");
                  setBlockOpen(false); setBlockNote("");
                  await load(); onChanged?.();
                } catch (e) {
                  toast.error(e?.response?.data?.error?.message || e?.response?.data?.detail || "Bildirilemedi");
                } finally { setBusy(false); }
              }}>
              {busy ? "..." : "Bildir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    <ImageLightbox
      items={lightboxIdx === null ? [] : (order?.reference_media || []).map(m => mediaUrl(API, m.url))}
      index={lightboxIdx || 0}
      onIndex={setLightboxIdx}
      onClose={() => setLightboxIdx(null)}
    />
    </>
  );
}

// Salt-okunur operasyon emri detayı — başka ekranlardan (örn. plan oluşturma) açmak için
export function OperationDetailViewer({ API, orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null); // referans görsel büyük önizleme (null=kapalı)
  // Mini harita 190px; konuma bakmak için büyütülebilmeli. Büyük emir detayında
  // bu vardı, salt-okunur "Görev Detayları" görünümünde unutulmuştu.
  const [mapBig, setMapBig] = useState(false);
  const apiBase = `${API}/api/operasyon`;

  useEffect(() => {
    let alive = true;
    axios.get(`${apiBase}/${orderId}`)
      .then(r => { if (alive) setOrder(r.data); })
      .catch(() => { toast.error("Operasyon detayı yüklenemedi"); onClose(); });
    return () => { alive = false; };
  }, [apiBase, orderId, onClose]);

  if (!order) return null;
  const si = statusInfo(order.status);
  const pi = priorityInfo(order.priority);
  const locs = (order.locations && order.locations.length > 0)
    ? order.locations
    : (order.location_label ? [{ label: order.location_label, lat: order.location_lat, lng: order.location_lng }] : []);

  return (
    <>
    <DraggableModal
      isOpen
      onClose={onClose}
      maxWidth="max-w-2xl"
      title="Görev Detayları"
      subtitle={order.title}
      footer={<div className="flex justify-end"><Button size="sm" variant="ghost" onClick={onClose}>Kapat</Button></div>}
    >
      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={si.cls}>{si.label}</Badge>
          <Badge variant="outline" className={pi.cls}>{pi.label}</Badge>
          {order.is_overdue && <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">Gecikti</Badge>}
          {order.order_type === "planlamaya_dahil" && order.planned_at &&
            <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">Planlandı: {fmtTR(order.scheduled_date)}</Badge>}
        </div>

        <div className="text-xs text-gray-500">Oluşturan: {order.created_by_name || "—"}</div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <div><span className="text-gray-400">Kategori:</span> {catLabel(order.task_category)}</div>
          {order.task_type && <div><span className="text-gray-400">Görev Türü:</span> {order.task_type}</div>}
          <div><span className="text-gray-400">Öncelik:</span> {pi.label}</div>
          <div><span className="text-gray-400">Emir Türü:</span> {orderTypeLabel(order.order_type)}</div>
          {order.operation_effect && <div><span className="text-gray-400">Operasyon Etkisi:</span> {effectLabel(order.operation_effect)}</div>}
          {order.source && <div><span className="text-gray-400">Kaynak:</span> {sourceLabel(order.source)}</div>}
          {order.scheduled_date && <div><span className="text-gray-400">Tarih:</span> {fmtTR(order.scheduled_date)}{order.start_time ? ` ${order.start_time}` : ""}{order.end_time ? `–${order.end_time}` : ""}</div>}
          {order.due_date && <div><span className="text-gray-400">Son Tarih:</span> {fmtTR(order.due_date)}</div>}
        </div>

        {evidenceList(order).length > 0 && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs">
            <span className="text-slate-500 font-medium">İstenen Kanıtlar:</span>{" "}
            {evidenceList(order).map((e, i) => (
              <Badge key={i} variant="outline" className="bg-white text-slate-700 border-slate-300 mr-1 mb-1">{e}</Badge>
            ))}
          </div>
        )}

        {order.description && (
          <div><span className="text-gray-400 text-xs">Açıklama:</span> <span className="text-gray-700">{order.description}</span></div>
        )}

        {locs.length > 0 && (
          <div className="text-xs">
            <span className="text-gray-400">Konum:</span>{" "}
            {locs.map((l, i) => {
              const c = locCoords(l);
              const label = l.label || l.kind || "konum";
              return (
                <span key={i} className="mr-2">
                  {c
                    ? <a href={mapsDirUrl(c[0], c[1])} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5" title="Yol tarifi al ve git">
                        <MapPin className="h-3 w-3" />{label}
                      </a>
                    : <span className="inline-flex items-center gap-0.5 text-gray-600"><MapPin className="h-3 w-3" />{label}</span>}
                  {l.note && <span className="text-amber-700" title="işaret notu"> — {l.note}</span>}
                </span>
              );
            })}
          </div>
        )}

        {/* Konum mini-haritası — marker'a tıkla yol tarifi, Büyüt ile büyük harita */}
        <LocationMiniMap order={order} onExpand={() => setMapBig(true)} />

        {Array.isArray(order.reference_media) && order.reference_media.length > 0 && (
          <div>
            <div className="font-semibold text-gray-700">Referans Görseller</div>
            <div className="text-[11px] text-gray-400 mb-1">Emri oluşturan ({order.created_by_name || "—"}) tarafından paylaşılan örnek görseller.</div>
            <div className="grid grid-cols-4 gap-2">
              {order.reference_media.map((m, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setLightboxIdx(idx)}
                  title="Büyük görüntüle"
                  className="block focus:outline-none"
                >
                  <img src={mediaUrl(API, m.url)} alt={m.name || "referans"} className="h-20 w-full object-cover rounded border hover:opacity-90 cursor-zoom-in" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <ImageLightbox
        items={lightboxIdx === null ? [] : (order?.reference_media || []).map(m => mediaUrl(API, m.url))}
        index={lightboxIdx || 0}
        onIndex={setLightboxIdx}
        onClose={() => setLightboxIdx(null)}
      />
    </DraggableModal>
    {mapBig && (
      <Dialog open onOpenChange={(o) => { if (!o) setMapBig(false); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {order.title} — Konum</DialogTitle></DialogHeader>
          <LocationMiniMap order={order} big />
          <p className="text-[11px] text-gray-400">İşaretli noktaya tıklayınca yol tarifi açılır.</p>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}

// Alt görevlendirme — ekip(ler)den işaretleyerek görevli seç + kim hangi kanıttan sorumlu
function DelegateDialog({ apiBase, orderId, subordinates, personnelId, ownerIds = new Set(), existingAssigneeIds = [], existingAssignees = [], scheduledDate, needStartEvidence = false, needEndEvidence = false, alreadyStartResponsible = false, alreadyEndResponsible = false, actorAlreadyProvides = false, evidenceLabels = [], onClose, onDone }) {
  // Daha önce alt görevlendirilen SAHA kişileri (park görevlisi) listede işaretli başlar —
  // formen duruma göre sonradan onları ekip sorumlusu (must_provide) yapabilsin diye.
  const [people, setPeople] = useState(() =>
    (existingAssignees || [])
      .filter(a => a.personnel_role === "park_gorevlisi" && (subordinates || []).some(p => p.id === a.personnel_id))
      .map(a => ({
        id: a.personnel_id,
        name: a.personnel_name || (subordinates || []).find(p => p.id === a.personnel_id)?.name || "—",
        start: !!a.must_provide_start_evidence,
        end: !!a.must_provide_end_evidence,
      }))
  ); // [{id, name, start, end}]
  // Zaten bir sorumlu (ör. önceki alt görevlendirmede Erdinç) varsa, yeni eklemede tekrar zorlama.
  const stillNeedStart = needStartEvidence && !alreadyStartResponsible;
  const stillNeedEnd = needEndEvidence && !alreadyEndResponsible;
  // Dağıtan (formen) "kanıtı ben yükleyeceğim" derse ekip sorumlusu seçme zorunluluğu kalkar.
  // Önceki dağıtımda zaten kendisi sorumlu olduysa tik DOLU açılır (ki yeni eklenen kişiyi
  // sistem otomatik sorumlu yapmasın).
  const [actorProvidesEvidence, setActorProvidesEvidence] = useState(actorAlreadyProvides);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set()); // açık ekip grupları (akordeon)
  const [unavail, setUnavail] = useState(() => new Map()); // personnel_id -> {reason_type, start_date, end_date}
  const { getTeamById } = useTeams();
  const toggleGroup = (id) => setOpenGroups(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // O tarihte izinli/raporlu olanları çek — seçilemez yapılır
  useEffect(() => {
    if (!scheduledDate) { setUnavail(new Map()); return; }
    let alive = true;
    axios.get(`${apiBase}/unavailable`, { params: { date: scheduledDate } })
      .then(r => { if (alive) setUnavail(new Map((Array.isArray(r.data) ? r.data : []).map(x => [x.personnel_id, x]))); })
      .catch(() => {});
    return () => { alive = false; };
  }, [apiBase, scheduledDate]);

  // Kendisi her zaman çıkar. Emirde zaten görevli olanlardan SADECE saha (park görevlisi)
  // olanları listede TUT (işaretli kalsın ki sonradan ekip sorumlusu yapılabilsin); formen/
  // yönetici olan mevcut görevlileri gizle.
  const sahaExistingIds = useMemo(
    () => new Set((existingAssignees || []).filter(a => a.personnel_role === "park_gorevlisi").map(a => a.personnel_id)),
    [existingAssignees]
  );
  const excluded = useMemo(
    () => new Set([personnelId, ...(existingAssigneeIds || []).filter(id => !sahaExistingIds.has(id))].filter(Boolean)),
    [personnelId, existingAssigneeIds, sahaExistingIds]
  );
  const candidates = useMemo(
    () => (subordinates || []).filter(p => p && p.id && !excluded.has(p.id)),
    [subordinates, excluded]
  );

  // Formen filtresi: emrin formenleri (müdürün seçtiği, ör. Inan/Vedat) arasından biri
  // seçilince yalnızca o formenin ekibi gösterilir; "all" → hepsi. BM ve MY için aynı.
  const [formenFilter, setFormenFilter] = useState("all");
  // Filtre: dağıtanın ALT formenleri (Vedat/İnan...). Birini seçince yalnız o formenin ekibi
  // (kendisi + çim/PG'leri); seçilmezse hepsi. Tek formen varsa filtre gizlenir.
  const orderFormen = useMemo(
    () => candidates.filter(p => roleOf(p) === "formen").map(p => ({ id: p.id, name: p.name })),
    [candidates]
  );
  const visibleCandidates = useMemo(
    () => formenFilter === "all" ? candidates : candidates.filter(p => p.formen_id === formenFilter || p.id === formenFilter),
    [candidates, formenFilter]
  );

  // Gruplama: Formenler → Çim/beceri ekipleri → Park görevlileri (sırayla).
  const teamGroups = useMemo(() => {
    const formenGrp = { id: "__formen", label: "Formenler", members: [] };
    const pgGrp = { id: "__pg", label: "Park görevlileri", members: [] };
    const skillMap = new Map();
    visibleCandidates.forEach(p => {
      if (roleOf(p) === "formen") { formenGrp.members.push(p); return; }
      const t = p.primary_team_id ? getTeamById(p.primary_team_id) : null;
      if (t && !t.is_default) {
        const base = t.display_name || t.slug;
        const label = /eki(p|bi)$/i.test(base) ? base : `${base} Ekibi`;
        if (!skillMap.has(t.id)) skillMap.set(t.id, { id: t.id, label, members: [] });
        skillMap.get(t.id).members.push(p);
      } else {
        pgGrp.members.push(p); // park görevlisi / default ekip
      }
    });
    const out = [];
    if (formenGrp.members.length) out.push(formenGrp);
    [...skillMap.values()].sort((a, b) => String(a.label).localeCompare(String(b.label), "tr")).forEach(g => out.push(g));
    if (pgGrp.members.length) out.push(pgGrp);
    return out;
  }, [visibleCandidates, getTeamById]);

  const isSel = (id) => people.some(p => p.id === id);
  const toggleMember = (m) => {
    if (unavail.has(m.id)) return; // izinli → seçilemez
    setPeople(prev => {
      if (prev.some(p => p.id === m.id)) return prev.filter(p => p.id !== m.id);
      // Tek kişiyse VE henüz sorumlu yoksa VE formen kanıtı kendisi yüklemiyorsa:
      // o kişiyi otomatik sorumlu yap (formen unutmasın). "Ben yükleyeceğim" seçiliyse
      // kutular boş gelir — formen isterse opsiyonel işaretler.
      const sole = prev.length === 0;
      return [...prev, { id: m.id, name: m.name, start: sole && stillNeedStart && !actorProvidesEvidence, end: sole && stillNeedEnd && !actorProvidesEvidence }];
    });
  };
  const toggleAll = (group) => {
    const selectable = group.members.filter(m => !unavail.has(m.id));
    const allSel = selectable.length > 0 && selectable.every(m => isSel(m.id));
    if (allSel) {
      const ids = new Set(selectable.map(m => m.id));
      setPeople(prev => prev.filter(p => !ids.has(p.id)));
    } else {
      setPeople(prev => {
        const have = new Set(prev.map(p => p.id));
        const fresh = selectable.filter(m => !have.has(m.id)).map(m => ({ id: m.id, name: m.name, start: false, end: false }));
        return [...prev, ...fresh];
      });
    }
  };
  const toggle = (id, key) => setPeople(prev => prev.map(p => p.id === id ? { ...p, [key]: !p[key] } : p));

  // Kanıt eksik mi? (formen kendisi yüklemiyorsa ve henüz sorumlu yoksa)
  const missingStart = !actorProvidesEvidence && stillNeedStart && !people.some(p => p.start);
  const missingEnd = !actorProvidesEvidence && stillNeedEnd && !people.some(p => p.end);
  const submit = async () => {
    if (people.length === 0) { toast.error("En az bir kişi işaretleyin"); return; }
    if (missingStart || missingEnd) {
      // Uyarı kartın altında zaten görünür; kısa hatırlatma
      toast.error("Kanıt sorumlusu seçin ya da 'Kanıtı ben yükleyeceğim'i işaretleyin.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${apiBase}/${orderId}/delegate`, {
        title: title || null, note: note || null,
        actor_provides_evidence: actorProvidesEvidence,
        assignees: people.map(p => ({
          kind: "person", personnel_id: p.id,
          must_provide_start_evidence: p.start, must_provide_end_evidence: p.end,
        })),
      });
      toast.success("Alt görevlendirme yapıldı");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Başarısız");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Alt Görevlendir / Ekip Seç</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-[11px] text-gray-500">Ekiplerden <b>gelecek olanları işaretle</b>. İşaretlilere istersen kanıt sorumluluğu ver. Listede sen ve emirde zaten görevli olanlar görünmez.</p>
          {/* Formen filtresi: emrin formenlerinden birini seç → yalnızca onun ekibi.
              Boş bırakılırsa hepsi görünür. */}
          {orderFormen.length > 0 && (
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">Formen:</Label>
              <SearchableSelect
                value={formenFilter}
                onChange={setFormenFilter}
                allValue="all"
                allLabel="Tüm formenler"
                searchPlaceholder="Formen ara..."
                className="h-8 flex-1"
                options={orderFormen.map(f => ({ id: f.id, name: f.name }))}
              />
            </div>
          )}
          {teamGroups.length === 0 && <p className="text-xs text-gray-400">{formenFilter !== "all" ? "Bu formenin eklenebilecek personeli yok." : "Eklenebilecek personel yok."}</p>}
          {teamGroups.map(g => {
            const allSel = g.members.every(m => isSel(m.id));
            const selCount = g.members.filter(m => isSel(m.id)).length;
            const open = openGroups.has(g.id);
            return (
              <div key={g.id} className="border rounded-md overflow-hidden">
                <button type="button" onClick={() => toggleGroup(g.id)}
                  className="w-full flex items-center justify-between bg-gray-50 px-2 py-1.5 hover:bg-gray-100">
                  <span className="font-medium text-xs flex items-center gap-1">
                    <span className="text-gray-400 w-3 inline-block">{open ? "▾" : "▸"}</span>
                    {g.label} ({g.members.length}){selCount > 0 ? ` · ${selCount} seçili` : ""}
                  </span>
                </button>
                {open && (
                <div className="divide-y">
                  <div className="flex justify-end px-2 py-1 bg-gray-50/50">
                    <button type="button" onClick={() => toggleAll(g)} className="text-[11px] text-emerald-700 hover:underline">
                      {allSel ? "Tümünü kaldır" : "Tümünü seç"}
                    </button>
                  </div>
                  {[...g.members].sort((a, b) => (ownerIds.has(b.id) ? 1 : 0) - (ownerIds.has(a.id) ? 1 : 0)).map(m => {
                    const sel = people.find(p => p.id === m.id);
                    const izin = unavail.get(m.id);
                    const isOwner = ownerIds.has(m.id);
                    return (
                      <div key={m.id} className={`px-2 py-1 ${izin ? "bg-gray-50" : ""}`}>
                        <label className={`flex items-center gap-2 ${izin ? "cursor-not-allowed text-gray-400" : "cursor-pointer"}`}>
                          <input type="checkbox" checked={!!sel} disabled={!!izin} onChange={() => toggleMember(m)} />
                          {isOwner && <span title="Bu işin bölgesi bu kişiye ait" aria-hidden="true">⭐</span>}
                          {/* İsim + (üst rolse rol) + takım. */}
                          {(() => {
                            const t = m.primary_team_id ? getTeamById(m.primary_team_id) : null;
                            const teamLbl = t && !t.is_default ? (t.display_name || t.slug) : null;
                            const roleLbl = roleOf(m) && roleOf(m) !== "park_gorevlisi" ? roleLabel(roleOf(m)) : null;
                            const meta = [roleLbl, teamLbl].filter(Boolean).join(" · ");
                            return <span>{m.name}{meta ? <span className="text-gray-400"> · {meta}</span> : null}</span>;
                          })()}
                          {(existingAssigneeIds || []).includes(m.id) && <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1" title="Bu görevde zaten görevli — istersen kanıt sorumluluğunu buradan değiştir">zaten görevli</span>}
                          {izin && <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">İzinli{izin.reason_type ? ` · ${izin.reason_type}` : ""}</span>}
                        </label>
                        {sel && !izin && (
                          <div className="ml-6 flex gap-3 mt-1 text-[11px] text-gray-600">
                            <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={sel.start} onChange={() => toggle(m.id, "start")} />Başlangıç kanıtı sorumlusu</label>
                            <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={sel.end} onChange={() => toggle(m.id, "end")} />Bitiş kanıtı sorumlusu</label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
          <div><Label>Başlık (opsiyonel — boşsa ana emir)</Label><Input value={title} onChange={e => setTitle(e.target.value)} /></div>
          <div><Label>Not (opsiyonel)</Label><Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} /></div>

          {/* Kanıt sorumluluğu — kartın en altında. Formen ya bir ekip sorumlusu işaretler
              ya da "kanıtı ben yükleyeceğim" der (işin başında durduğunda mantıklı). */}
          {(needStartEvidence || needEndEvidence) && (
            <div className={`rounded-md border p-2.5 space-y-2 ${(missingStart || missingEnd) ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-gray-50"}`}>
              <div className="text-[11px] text-gray-700 flex items-start gap-1.5">
                <AlertTriangle className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${(missingStart || missingEnd) ? "text-amber-600" : "text-gray-400"}`} />
                <span><b>Bu görev kanıt istiyor:</b> {evidenceLabels.join(", ")}.</span>
              </div>
              {(missingStart || missingEnd) && (
                <div className="text-[11px] text-amber-800 pl-5">
                  {missingStart && "Başlangıç"}{missingStart && missingEnd && " ve "}{missingEnd && "Bitiş"} için
                  {" "}<b>en az bir kişiyi sorumlu</b> işaretle — ya da aşağıdan kanıtı kendin yükle. Yoksa kaydedilmez.
                </div>
              )}
              {!actorProvidesEvidence && !(stillNeedStart || stillNeedEnd) && (
                <div className="text-[11px] text-gray-400 pl-5">Kanıt sorumlusu zaten atanmış — bu eklemede sorumlu seçmek opsiyonel.</div>
              )}
              <label className="flex items-start gap-2 cursor-pointer text-[12px] text-gray-700 bg-white rounded-md border px-2 py-1.5">
                <input type="checkbox" checked={actorProvidesEvidence}
                       onChange={e => {
                         const v = e.target.checked;
                         setActorProvidesEvidence(v);
                         setPeople(prev => {
                           if (v) {
                             // Kendisi yükleyecek → seçili kişilerin sorumlu kutularını boşalt (opsiyonel)
                             return prev.map(p => ({ ...p, start: false, end: false }));
                           }
                           // Tiki kaldırdı → kanıtı birinin yüklemesi lazım; kimse sorumlu değilse ilk kişiyi sorumlu yap
                           if (prev.length === 0) return prev;
                           const hasStart = prev.some(p => p.start), hasEnd = prev.some(p => p.end);
                           return prev.map((p, i) => i === 0 ? {
                             ...p,
                             start: p.start || (stillNeedStart && !hasStart),
                             end: p.end || (stillNeedEnd && !hasEnd),
                           } : p);
                         });
                       }} className="mt-0.5" />
                <span><b>Kanıtı ben yükleyeceğim</b> — işin başında durup istenen kanıtları kendim yüklerim, ekip sorumlusu seçmem gerekmez.</span>
              </label>
              {actorProvidesEvidence && (
                <div className="text-[11px] text-emerald-700 pl-1">✓ Kanıt sorumluluğu sende — Başla/Tamamla ederken istenen kanıtı sen yükleyeceksin.</div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>İptal</Button>
          <Button onClick={submit} disabled={busy || people.length === 0} className="bg-green-600 hover:bg-green-700">{busy ? "..." : `Görevlendir${people.length ? ` (${people.length})` : ""}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Planlamaya dahil et — görevi yapılacağı tarihe planla (Yol A)
function PlanDialog({ apiBase, orderId, initial, onClose, onDone }) {
  const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const rel = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return isoOf(d); };
  const nextMonday = () => { const d = new Date(); const add = ((8 - d.getDay()) % 7) || 7; d.setDate(d.getDate() + add); return isoOf(d); };
  const [date, setDate] = useState(initial || todayISO());
  const [busy, setBusy] = useState(false);

  const quick = [
    { label: "Bugün", val: rel(0) },
    { label: "Yarın", val: rel(1) },
    { label: "Gelecek hafta", val: nextMonday() },
  ];

  const submit = async () => {
    if (!date) { toast.error("Tarih seçin"); return; }
    setBusy(true);
    try {
      await axios.post(`${apiBase}/${orderId}/plan`, { date });
      toast.success("Planlamaya dahil edildi");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Planlanamadı");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Planlamaya Dahil Et</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-gray-500">
            Bu emri yapacağın günü seç. O günün haftalık plan görünümünde bu emir görünür;
            haftan doluysa daha sonraki bir tarihe planlayabilirsin.
          </p>
          <div className="flex flex-wrap gap-2">
            {quick.map(q => (
              <button key={q.label} type="button" onClick={() => setDate(q.val)}
                className={`px-2.5 py-1 rounded border text-xs ${date === q.val ? "bg-blue-600 text-white border-blue-700" : "bg-white text-gray-700 hover:bg-gray-50"}`}>
                {q.label}
              </button>
            ))}
          </div>
          <div>
            <Label>Tarih</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-9" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>İptal</Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? "..." : "Planla"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Operasyon SLA ayarları (süper admin — operasyon.sla_yonet)
function SlaConfigDialog({ API, authz, features, onFeatures, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const canManageFeatures = authz?.has?.("operasyon.ayar_yonet");
  const [execModel, setExecModel] = useState(features?.executor_model !== false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/operasyon/sla-config`)
      .then(r => { if (alive) setCfg(r.data); })
      .catch(() => { toast.error("SLA ayarı yüklenemedi"); onClose(); });
    return () => { alive = false; };
  }, [API, onClose]);

  const setBlock = (prio, key, val) =>
    setCfg(c => ({ ...c, [prio]: { ...c[prio], [key]: Math.max(0, parseInt(val || 0, 10) || 0) } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/operasyon/sla-config`, cfg);
      if (canManageFeatures && execModel !== (features?.executor_model !== false)) {
        const r = await axios.put(`${API}/api/operasyon/features`, { executor_model: execModel });
        onFeatures?.({ executor_model: r.data?.executor_model !== false });
      }
      toast.success("Operasyon ayarları kaydedildi");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  if (!cfg) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Operasyon Ayarları</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          {canManageFeatures && (
            <div className="border rounded p-2 bg-sky-50/50">
              <div className="font-semibold mb-1">Özellikler</div>
              <Chk label="Yürütücü/Bilgi modeli açık" v={execModel} on={() => setExecModel(v => !v)} />
              <p className="text-[11px] text-gray-500 mt-1">
                Açıkken: görevliler arasında en alt rütbeli = <b>yürütücü</b> (Başla/Tamamla/İş Engeli);
                üstündeki roller = <b>bilgi</b> (sadece izler + personel ekle/çıkar/iptal). Kapalıyken: her görevli Başla/Tamamla görür.
              </p>
            </div>
          )}
          <div className="font-semibold pt-1">SLA / Eskalasyon</div>
          <Chk label="SLA eskalasyonu açık" v={cfg.enabled} on={() => setCfg(c => ({ ...c, enabled: !c.enabled }))} />
          <p className="text-xs text-gray-500">
            Süreler dakika cinsindendir. Süresinde görülmeyen/başlanmayan emir, oluşturanın üst amirine otomatik bildirilir. 0 = kapalı.
          </p>
          {[["cok_acil", "🔴 Çok Acil"], ["acil", "🟠 Acil"]].map(([prio, label]) => (
            <div key={prio} className="border rounded p-2">
              <div className="font-semibold mb-1">{label}</div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Görülme (dk)</Label><Input type="number" min="0" value={cfg[prio]?.seen_min ?? 0} onChange={e => setBlock(prio, "seen_min", e.target.value)} /></div>
                <div><Label>Başlama (dk)</Label><Input type="number" min="0" value={cfg[prio]?.start_min ?? 0} onChange={e => setBlock(prio, "start_min", e.target.value)} /></div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>İptal</Button>
          <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700">{saving ? "..." : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Görevli kanıtları + geofence durumu (detayda)
function EvidenceRow({ API, label, items, geo }) {
  const [lbIdx, setLbIdx] = useState(null); // büyük önizleme (null=kapalı)
  const media = Array.isArray(items) ? items : [];
  const lbItems = media.map(m => ({ url: mediaUrl(API, m.url), type: m.type, name: m.name }));
  return (
    <div className="mt-1">
      <div className="flex items-center gap-2">
        <span className="text-gray-500">{label}:</span>
        {geo === false && <span className="text-amber-600 flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" />Konum doğrulanamadı</span>}
        {geo === true && <span className="text-green-600 flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" />Konum doğrulandı</span>}
      </div>
      {media.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-0.5">
          {media.map((m, i) => (
            <button key={i} type="button" onClick={() => setLbIdx(i)} title="Büyük görüntüle" className="block focus:outline-none">
              {m.type === "video" ? (
                <span className="relative inline-block">
                  <video src={mediaUrl(API, m.url)} muted preload="metadata" className="h-16 w-16 object-cover rounded border cursor-zoom-in" />
                  <span className="absolute inset-0 flex items-center justify-center text-white text-lg drop-shadow">▶</span>
                </span>
              ) : (
                <img src={mediaUrl(API, m.url)} alt="" className="h-16 w-16 object-cover rounded border hover:opacity-90 cursor-zoom-in" />
              )}
            </button>
          ))}
        </div>
      )}
      <ImageLightbox
        items={lbIdx === null ? [] : lbItems}
        index={lbIdx || 0}
        onIndex={setLbIdx}
        onClose={() => setLbIdx(null)}
      />
    </div>
  );
}

// Başla / Tamamla — kanıt (foto/video) + GPS yakalama + (tamamla: sonuç/gecikme)
function EvidenceActionDialog({ API, apiBase, orderId, order, me, phase, onClose, onDone }) {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [note, setNote] = useState("");
  const [result, setResult] = useState("tamamlandi");
  const [delayReason, setDelayReason] = useState("");
  const [affected, setAffected] = useState([]); // [{label, effect}]
  const [busy, setBusy] = useState(false);

  // Kanıt alanları başlangıç/bitiş olarak adlandırılır: phase "complete" → "end"
  const evPrefix = phase === "complete" ? "end" : "start";
  // Kanıt YALNIZCA bu kişi sorumlu (must_provide) ise zorunludur; sorumlu değilse
  // (örn. alt görevde kanıt sorumlusu seçilmemiş kişi) kanıtsız başlar/tamamlar.
  const responsible = phase === "complete" ? !!me?.must_provide_end_evidence : !!me?.must_provide_start_evidence;
  const needPhoto = !!order[`need_${evPrefix}_photo`] && responsible;
  const needVideo = !!order[`need_${evPrefix}_video`] && responsible;
  const needGps = !!order[`need_${evPrefix}_gps`] && responsible;
  const requestedItems = [
    needPhoto && "Fotoğraf",
    needVideo && "Video",
    needGps && "Konum (GPS)",
  ].filter(Boolean);

  const upload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await axios.post(`${API}/api/upload-revision-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (r.data?.url) added.push({ url: r.data.url, type: (file.type || "").startsWith("video") ? "video" : "photo", name: file.name });
      }
      if (added.length) setMedia(m => [...m, ...added]);
    } catch (e) { toast.error("Yüklenemedi"); } finally { setUploading(false); }
  };
  const getGps = () => {
    if (!navigator.geolocation) { toast.error("Tarayıcı konum desteklemiyor"); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsLoading(false); toast.success("Konum alındı"); },
      () => { setGpsLoading(false); toast.error("Konum alınamadı (izin gerekebilir)"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  const submit = async () => {
    const kinds = new Set(media.map(m => m.type));
    if (needPhoto && !kinds.has("photo")) { toast.error("Fotoğraf zorunlu"); return; }
    if (needVideo && !kinds.has("video")) { toast.error("Video zorunlu"); return; }
    if (needGps && !gps) { toast.error("Konum (GPS) zorunlu"); return; }
    setBusy(true);
    try {
      const body = { media, note: note || null, gps_lat: gps?.lat ?? null, gps_lng: gps?.lng ?? null };
      if (phase === "complete") {
        body.completion_result = result;
        if (delayReason) body.delay_reason = delayReason;
        body.affected_plans = affected
          .filter(a => (a.label || "").trim())
          .map(a => ({ activity_label: a.label.trim(), effect: a.effect }));
      }
      await axios.post(`${apiBase}/${orderId}/${phase}`, body);
      toast.success(phase === "start" ? "Başlatıldı" : "Tamamlandı");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "İşlem başarısız");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{phase === "start" ? "Göreve Başla" : "Görevi Tamamla"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label>Fotoğraf / Video {(needPhoto || needVideo) ? "— zorunlu" : "(opsiyonel)"}</Label>
            {requestedItems.length > 0 && (
              <p className="text-[11px] text-amber-700 mb-1">Müdürün istediği kanıt: {requestedItems.join(", ")}</p>
            )}
            <input type="file" accept="image/*,video/*" multiple disabled={uploading}
              onChange={e => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} className="text-sm block mt-1" />
            {uploading && <span className="text-xs text-gray-500">Yükleniyor...</span>}
            <div className="flex flex-wrap gap-1 mt-1">
              {media.map((m, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-gray-100 rounded px-1.5 py-0.5">
                  {m.type === "video" ? "🎬" : "🖼"} {m.name || m.type}
                  <button type="button" onClick={() => setMedia(arr => arr.filter((_, x) => x !== i))} className="hover:text-red-600"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>
          <div>
            <Label>Konum (GPS){needGps ? " — zorunlu" : " (opsiyonel)"}</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button type="button" variant="outline" size="sm" onClick={getGps} disabled={gpsLoading}>
                <MapPin className="h-4 w-4 mr-1" />{gpsLoading ? "Alınıyor..." : "Konumu Al"}
              </Button>
              {gps && <span className="text-xs text-gray-500">{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</span>}
            </div>
          </div>
          {phase === "complete" && (
            <>
              <div>
                <Label>Sonuç</Label>
                <select className="w-full border rounded-md h-9 px-2 text-sm" value={result} onChange={e => setResult(e.target.value)}>
                  <option value="tamamlandi">Tamamlandı</option>
                  <option value="kismen">Kısmen Tamamlandı</option>
                  <option value="tamamlanamadi">Tamamlanamadı</option>
                </select>
              </div>
              <div>
                <Label>Gecikme Nedeni (varsa)</Label>
                <select className="w-full border rounded-md h-9 px-2 text-sm" value={delayReason} onChange={e => setDelayReason(e.target.value)}>
                  <option value="">— Yok —</option>
                  <option value="yagis">Yağış</option>
                  <option value="personel">Personel Eksikliği</option>
                  <option value="ekipman">Ekipman Arızası</option>
                  <option value="vatandas">Vatandaş Yoğunluğu</option>
                  <option value="diger">Diğer</option>
                </select>
              </div>
              <div>
                <Label>Operasyon nedeniyle etkilenen planlı işler (opsiyonel)</Label>
                <p className="text-[11px] text-gray-400">Ertelenen işler "eksik / performans kaybı" sayılmaz.</p>
                {affected.map((a, i) => (
                  <div key={i} className="flex gap-2 mt-1">
                    <Input value={a.label} className="flex-1"
                      onChange={e => setAffected(arr => arr.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))}
                      placeholder="örn. Sulama" />
                    <select className="border rounded-md h-9 px-2 text-sm" value={a.effect}
                      onChange={e => setAffected(arr => arr.map((x, xi) => xi === i ? { ...x, effect: e.target.value } : x))}>
                      <option value="ertelendi">Ertelendi</option>
                      <option value="etkilenmedi">Etkilenmedi</option>
                    </select>
                    <button type="button" onClick={() => setAffected(arr => arr.filter((_, xi) => xi !== i))} className="text-gray-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="mt-1"
                  onClick={() => setAffected(arr => [...arr, { label: "", effect: "ertelendi" }])}>
                  <Plus className="h-3 w-3 mr-1" />İş ekle
                </Button>
              </div>
            </>
          )}
          <div>
            <Label>Not (opsiyonel)</Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>İptal</Button>
          <Button onClick={submit} disabled={busy} className="bg-green-600 hover:bg-green-700">{busy ? "..." : (phase === "start" ? "Başlat" : "Tamamla")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
